import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { 
  FileText, 
  Download, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle,
  Scale,
  Loader2,
  ChevronDown,
  ChevronRight,
  Vault,
  Save,
  Upload,
  File,
  X,
  FileDown,
  Gavel,
  User
} from 'lucide-react';
import axios from 'axios';
import MailingTracker, { MailingProgressTracker } from './MailingTracker';

const WorkflowBuilder = ({ conversationId, user, onDocumentSaved, onSwitchToStrategy }) => {
  // Case Type Selection - NEW
  const [caseType, setCaseType] = useState(''); // 'petitioner', 'defendant', or 'debt_discharge'
  
  // Client Information
  const [clientInfo, setClientInfo] = useState({
    fullName: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    email: '',
    phone: '',
    country: '' // NEW: Country selection for UK-specific logic
  });
  // Opposing Party Information (for remedy generation)
  const [opposingPartyInfo, setOpposingPartyInfo] = useState({
    fullName: '',
    fullAddress: ''
  });
  
  const [selectedCategory, setSelectedCategory] = useState('');
  const [caseDescription, setCaseDescription] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [remedy, setRemedy] = useState(null);
  const [isGeneratingDocuments, setIsGeneratingDocuments] = useState(false);
  const [isGeneratingTab3Documents, setIsGeneratingTab3Documents] = useState(false);
  const [generatedDocuments, setGeneratedDocuments] = useState(null);
  const [generatedTab3Documents, setGeneratedTab3Documents] = useState(null);
  const [generatedOpportunityCureDoc, setGeneratedOpportunityCureDoc] = useState(null);
  const [isGeneratingOpportunityCure, setIsGeneratingOpportunityCure] = useState(false);
  const [respondentResponse2, setRespondentResponse2] = useState(null); // Response upload for Second Mailing
  const [respondentResponse3, setRespondentResponse3] = useState(null); // Response upload for Third Mailing
  const [isUploadingResponse, setIsUploadingResponse] = useState(false);
  const [currentPromptTemplate, setCurrentPromptTemplate] = useState('');
  const [generatedDebtDischargeDocuments, setGeneratedDebtDischargeDocuments] = useState(null);
  const [isGeneratingDebtDischargeDocuments, setIsGeneratingDebtDischargeDocuments] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    legalOverview: true,  // Tab 1: Case Matter Overview 
    executingRemedy: false,  // Tab 2: Executing your Remedy (NEW)
    equitableRights: false,  // Tab 3: Enforcing Your Protections: First Mailing
    opportunityToCure: false, // Tab 3.5: Opportunity to Cure: Second Mailing
    administrativeProcess: false,  // Tab 4: Administrative Default: Third Mailing
    escalateToCourt: false,  // Tab 6: Escalate to Court
  });
  const { toast } = useToast();

  const API_BASE = process.env.REACT_APP_BACKEND_URL+"/api";

  // Default prompt template following user's three-section framework
  const DEFAULT_PROMPT = `Generate a comprehensive Common Law Barrister analysis with exactly THREE SECTIONS:

SECTION 1: LEGAL MATTER OVERVIEW
- State the matter and analyze under Common Law, Equity, and Constitutional principles
- Identify jurisdiction & standing (Common Law vs corporate fiction)
- Detail English Common Law violations (no harm/no crime, due process, property rights, etc.)
- Highlight Equity Jurisprudence violations (Maxims of Equity breaches)
- Reference Magna Carta & English Bill of Rights (1689) protections
- Show U.S. Constitutional parallels (Article I Section 10, 4th/5th/6th/7th Amendments)
- Provide final overview with remedy in honor

SECTION 2: ENFORCE YOUR EQUITABLE RIGHTS
- Recognition of the matter presented
- Affidavit of Status for living man/woman (not legal fiction)
- Testimony Under God with solemn declaration
- Conditional Acceptance upon proof of claim with full accounting
- Article I Section 10 Clause 1 assertion ("No State shall make any Thing but gold and silver Coin a Tender in Payment of Debts")
- Emphasize lawful payment & equity (since 1933 no lawful medium, discharge only)
- Document production for service with settlement in honor

SECTION 3: EXECUTING THE ADMINISTRATIVE PROCESS  
- Explain Article I Section 10 Clause 1 (gold/silver requirement, equitable discharge)
- Detail Administrative Process purpose (private dispute resolution)
- 5-step process: (1)Notice of Conditional Acceptance, (2)Second Notice/Opportunity to Cure, (3)Notice of Default, (4)Certificate of Non-Response, (5)Remedy & Enforcement
- Explain administrative default importance (burden shift, enforceable record)
- Close with remedy in honor (peaceful settlement via Conditional Acceptance)

CRITICAL: All fee schedules must specify payment ONLY in lawful money (constitutional gold and silver coin) with explicit rejection of Federal Reserve Notes.

Generate all three sections with detailed analysis for each requirement.`;

  const getPromptTemplate = () => {
    const savedPrompt = localStorage.getItem('remedy_prompt_template');
    return savedPrompt || DEFAULT_PROMPT;
  };

  // Load prompt template on component mount and listen for updates
  useEffect(() => {
    setCurrentPromptTemplate(getPromptTemplate());
    
    const handlePromptUpdate = (event) => {
      setCurrentPromptTemplate(event.detail.promptTemplate);
    };
    
    window.addEventListener('promptTemplateUpdated', handlePromptUpdate);
    
    // Check for loadMatter parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const loadMatterId = urlParams.get('loadMatter');
    if (loadMatterId) {
      loadExistingMatter(loadMatterId);
    }
    
    return () => {
      window.removeEventListener('promptTemplateUpdated', handlePromptUpdate);
    };
  }, []);

  // Load conversation data when conversationId changes
  useEffect(() => {
    if (conversationId && conversationId !== 'new') {
      loadConversationData(conversationId);
    } else {
      // Reset state for new conversation - DO NOT auto-select case type
      resetWorkflowState();
      setCaseType(''); // Always start with empty case type for new case matters
    }
  }, [conversationId]);

  const resetWorkflowState = () => {
    // Reset all workflow state EXCEPT caseType - caseType is handled separately
    setClientInfo({
      fullName: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      email: '',
      phone: '',
      country: ''
    });
    setOpposingPartyInfo({
      fullName: '',
      fullAddress: ''
    });
    setSelectedCategory('');
    setCaseDescription('');
    setRemedy(null);
    setUploadedDocuments([]);
    setGeneratedDocuments(null);
    setGeneratedTab3Documents(null);
    setGeneratedDebtDischargeDocuments(null);
  };

  const loadConversationData = async (convId) => {
    try {
      // ALWAYS reset state first when switching conversations to prevent mixing
      resetWorkflowState();
      setCaseType(''); // Clear previous conversation's case type
      
      // Only load if it's an existing conversation (not new)
      if (convId === 'new' || !convId) {
        // For new conversations, ALWAYS ask user to choose - don't auto-select
        return;
      }

      // Try to load conversation data
      const response = await axios.get(`${API_BASE}/conversations/${convId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data) {
        const conversation = response.data;
        
        // Check if this is truly a new conversation (no messages or no meaningful content)
        const messages = conversation.messages || [];
        const hasRemedyData = messages.some(msg => 
          msg.role === 'assistant' && 
          msg.content && 
          (msg.content.includes('SECTION 1') || msg.content.includes('LEGAL MATTER OVERVIEW'))
        );
        
        // Also check metadata for remedy data flag
        const metadata = conversation.metadata || {};
        const hasMetadataRemedy = metadata.has_remedy_data || metadata.remedy_content;
        
        // If no remedy data exists and no metadata, treat as new conversation
        if (!hasRemedyData && !hasMetadataRemedy && Object.keys(metadata).length === 0) {
          // For new conversations, ALWAYS ask user to choose - don't auto-select
          return;
        }
        
        // Restore data if there's meaningful content - ALWAYS restore THIS conversation's data
        if (hasRemedyData || hasMetadataRemedy || Object.keys(metadata).length > 0) {
          const lastRemedyMessage = messages
            .filter(msg => msg.role === 'assistant' && msg.content && msg.content.includes('SECTION 1'))
            .pop();

          // ALWAYS restore THIS conversation's case type first - this is permanent for THIS legal matter
          if (metadata.case_type) {
            setCaseType(metadata.case_type); // This is permanent for THIS specific legal matter
          }
          
          // Then restore other metadata for THIS conversation
          if (metadata.category) setSelectedCategory(metadata.category);
          if (metadata.case_description) setCaseDescription(metadata.case_description);
          if (metadata.client_info) setClientInfo(metadata.client_info);
          if (metadata.opposing_party_info) setOpposingPartyInfo(metadata.opposing_party_info);
          if (metadata.uploaded_documents) setUploadedDocuments(metadata.uploaded_documents);
          
          // Restore remedy data - check both message and metadata
          if (lastRemedyMessage) {
            const remedyData = {
              category: metadata.category || '',
              fullResponse: lastRemedyMessage.content,
              timestamp: lastRemedyMessage.timestamp,
              conversationId: convId,
              uploadedDocuments: metadata.uploaded_documents || []
            };
            setRemedy(remedyData);
          } else if (metadata.remedy_content) {
            // Restore from metadata if no message found
            const remedyData = {
              category: metadata.category || '',
              fullResponse: metadata.remedy_content,
              timestamp: metadata.last_remedy_generated || new Date().toISOString(),
              conversationId: convId,
              uploadedDocuments: metadata.uploaded_documents || []
            };
            setRemedy(remedyData);
          }
          
          // Always restore generated documents if available (must have actual content, not empty object)
          if (metadata.generated_documents && Object.keys(metadata.generated_documents).length > 0) {
            setGeneratedDocuments(metadata.generated_documents);
          }
          if (metadata.generated_tab3_documents && Object.keys(metadata.generated_tab3_documents).length > 0) {
            setGeneratedTab3Documents(metadata.generated_tab3_documents);
          }
          if (metadata.generated_opportunity_cure_doc) {
            setGeneratedOpportunityCureDoc(metadata.generated_opportunity_cure_doc);
          }
        } else {
          // For truly new conversations, ALWAYS ask user to choose - don't auto-select
        }
      }
      
      // Also check legal matters database for this conversation and ALWAYS restore if found
      const legalMattersResponse = await axios.get(`${API_BASE}/legal-matters?conversation_id=${convId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (legalMattersResponse.data && legalMattersResponse.data.length > 0) {
        const matter = legalMattersResponse.data[0]; // Get the first (most recent) legal matter
        
        // Always restore legal matter data when returning to existing conversation
        // Only restore case_type from legal matter if not already set from conversation metadata
        const currentCaseType = caseType; // Check current state
        if (matter.case_type && !currentCaseType) {
          setCaseType(matter.case_type);
        }
        if (matter.category) setSelectedCategory(matter.category);
        if (matter.case_description) setCaseDescription(matter.case_description);
        if (matter.client_info) setClientInfo(matter.client_info);
        if (matter.opposing_party_info) setOpposingPartyInfo(matter.opposing_party_info);
        if (matter.uploaded_documents) setUploadedDocuments(matter.uploaded_documents);
        
        // Always restore remedy content if it exists
        if (matter.remedy_content) {
          const remedyData = {
            category: matter.category,
            fullResponse: matter.remedy_content,
            timestamp: matter.updated_at,
            conversationId: convId,
            uploadedDocuments: matter.uploaded_documents || []
          };
          setRemedy(remedyData);
        }
        
        // Always restore generated documents
        if (matter.generated_documents && Object.keys(matter.generated_documents).length > 0) {
          setGeneratedDocuments(matter.generated_documents);
        }
        if (matter.generated_tab3_documents && Object.keys(matter.generated_tab3_documents).length > 0) {
          setGeneratedTab3Documents(matter.generated_tab3_documents);
        }
      }
      
    } catch (error) {
      console.error('Error loading conversation data:', error);
      // If there's an error loading, start fresh
      resetWorkflowState();
    }
  };

  const loadExistingMatter = async (matterId) => {
    try {
      const response = await axios.get(`${API_BASE}/legal-matters/${matterId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data) {
        const matter = response.data;
        
        // Populate the form with existing data
        setSelectedCategory(matter.category);
        setCaseDescription(matter.case_description);
        if (matter.client_info) setClientInfo(matter.client_info);
        if (matter.opposing_party_info) setOpposingPartyInfo(matter.opposing_party_info);
        
        // If there are uploaded documents, restore them
        if (matter.uploaded_documents && matter.uploaded_documents.length > 0) {
          setUploadedDocuments(matter.uploaded_documents.map(doc => ({
            ...doc,
            id: Date.now() + Math.random() // Generate new ID for frontend state
          })));
        }

        // If there's remedy content, restore it
        if (matter.remedy_content) {
          const remedyData = {
            category: matter.category,
            fullResponse: matter.remedy_content,
            timestamp: matter.updated_at,
            conversationId: conversationId,
            uploadedDocuments: matter.uploaded_documents || []
          };
          setRemedy(remedyData);
        }

        // If there are generated documents, restore them
        if (matter.generated_documents && Object.keys(matter.generated_documents).length > 0) {
          setGeneratedDocuments(matter.generated_documents);
        }

        if (matter.generated_tab3_documents && Object.keys(matter.generated_tab3_documents).length > 0) {
          setGeneratedTab3Documents(matter.generated_tab3_documents);
        }

        toast({
          title: "Legal Matter Loaded",
          description: `Loaded existing matter: ${matter.title}`,
        });

        // Clear the URL parameter
        const newUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    } catch (error) {
      console.error('Failed to load existing matter:', error);
      toast({
        title: "Load Failed",
        description: "Could not load the requested legal matter",
        variant: "destructive"
      });
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    const token = localStorage.getItem('token');
    
    for (const file of files) {
      // Basic file validation
      const maxSize = 50 * 1024 * 1024; // 50MB
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'image/jpeg',
        'image/png'
      ];
      
      if (file.size > maxSize) {
        toast({
          title: "File Too Large",
          description: `${file.name} exceeds 50MB limit`,
          variant: "destructive"
        });
        continue;
      }
      
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid File Type",
          description: `${file.name} is not a supported file type`,
          variant: "destructive"
        });
        continue;
      }

      // Upload to backend for text extraction
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tag', 'case_document');
        formData.append('description', 'Uploaded for AI analysis in case matter');

        toast({ title: "Analyzing...", description: `Extracting text from ${file.name}` });

        const res = await axios.post(
          `${API_BASE}/attorney/case-documents/${conversationId}/upload`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );

        const extractedText = res.data.extracted_text || '';

        const newDocument = {
          id: res.data.doc_id || Date.now() + Math.random(),
          name: file.name,
          size: `${(file.size / 1024).toFixed(1)} KB`,
          type: file.type,
          extractedText: extractedText,
          uploadedAt: new Date().toISOString()
        };

        setUploadedDocuments(prev => [...prev, newDocument]);

        toast({
          title: extractedText ? "Document Analyzed" : "Document Uploaded",
          description: extractedText
            ? `${file.name} — extracted ${extractedText.length} characters for AI analysis`
            : `${file.name} uploaded (no extractable text found)`,
        });
      } catch (err) {
        console.error('Upload error:', err);
        toast({
          title: "Upload Failed",
          description: `Could not process ${file.name}: ${err.response?.data?.detail || 'Unknown error'}`,
          variant: "destructive"
        });
      }
    }
    
    // Clear the input
    event.target.value = '';
  };

  const removeDocument = (documentId) => {
    setUploadedDocuments(prev => prev.filter(doc => doc.id !== documentId));
    toast({
      title: "Document Removed",
      description: "Document has been removed from analysis",
    });
  };

  // Defendant Categories (existing)
  const defendantCategories = [
    { value: 'traffic', label: 'Traffic', icon: '🚗' },
    { value: 'property', label: 'Property', icon: '🏠' },
    { value: 'contract', label: 'Contract', icon: '📋' },
    { value: 'criminal-defense', label: 'Criminal Defense', icon: '⚖️' },
    { value: 'family', label: 'Family', icon: '👨‍👩‍👧‍👦' },
    { value: 'taxation', label: 'Taxation', icon: '💰' },
    { value: 'estate-trusts', label: 'Estate/Trusts', icon: '📜' }
  ];

  // Petitioner Categories (new)
  const petitionerCategories = [
    { value: 'release-custody-bail', label: 'Release of Custody / Bail', icon: '🔓', description: 'Asking the judge to reduce or eliminate bail, or requesting release on personal recognizance' },
    { value: 'dismissal-charges', label: 'Dismissal of Charges / Case', icon: '❌', description: 'Asking the judge to throw out charges due to lack of evidence, jurisdictional defects, or constitutional violations' },
    { value: 'modification-orders', label: 'Modification of Court Orders', icon: '📝', description: 'Child support, custody/visitation arrangements, alimony/spousal support adjustments' },
    { value: 'restoration-rights', label: 'Restoration of Rights', icon: '🔄', description: 'Petition to expunge/seal records, restore firearm rights, restore driving privileges' },
    { value: 'equitable-relief', label: 'Equitable Relief / Emergency Protection', icon: '🛡️', description: 'Petition for injunctions, protective orders (domestic violence, stalking, harassment)' },
    { value: 'probate-estate', label: 'Probate & Estate Matters', icon: '📋', description: 'Petition to administer estate, appoint guardian/conservator' },
    { value: 'civil-disputes', label: 'Civil Disputes', icon: '⚖️', description: 'Petition to enforce contract/judgment, compel arbitration or compliance' },
    { value: 'extraordinary-writs', label: 'Extraordinary Writs', icon: '📜', description: 'Writ of Habeas Corpus, Mandamus, Prohibition' }
  ];

  // Debt Discharge Categories (new)
  const debtDischargeCategories = [
    { value: 'consumer-contract', label: 'Consumer Contract', icon: '📜' },
    { value: 'tax-collection', label: 'Tax Collection', icon: '💰' }
  ];

  // Notice of Conditional Acceptance Templates for Debt Discharge
  const getDebtDischargeConditionalAcceptanceTemplate = () => {
    const clientName = clientInfo.fullName || '[Your Full Name]';
    const clientAddress = clientInfo.streetAddress || '[Your Mailing Location]';
    const clientCity = clientInfo.city || '[City]';
    const clientState = clientInfo.state || '[State]';
    const clientZip = clientInfo.zipCode || '[ZIP]';
    const opposingParty = opposingPartyInfo.fullName || '[Name of Debt Collector / Agency]';
    const opposingAddress = opposingPartyInfo.fullAddress || '[Address]';
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'long', 
      year: 'numeric' 
    });

    if (selectedCategory === 'tax-collection') {
      return `Notice of Conditional Acceptance (Non-Dispute; Lawful Payment Inquiry – Tax Collection Agency)

${clientName}, in Sui Juris
c/o ${clientAddress}
${clientCity}, ${clientState} ${clientZip}
${formattedDate}
Via Certified Mail No.: [_________]

To:
${opposingParty}
Attn: [Specific Office or Agent, if named]
${opposingAddress}

Re: Notice of Conditional Acceptance (Non-Dispute; Lawful Payment Inquiry)
Reference: Alleged Tax Liability / Account / Case No. [___________]
Your Notice(s) dated: [list their letter dates]

1. Standing & Purpose
I, ${clientName}, a living man/woman, standing in peace and honor, issue this Notice of Conditional Acceptance (Non-Dispute; Lawful Payment Inquiry) in response to your presentments alleging tax liabilities, including but not limited to:
Notices, demands, or bills referencing "Account / SSN / EIN / Case No." [___________], and
Any alleged balance(s) for years [ – ].
This Notice is made without prejudice, reserving all unalienable rights at law and in equity, including rights secured by the Constitution for the United States of America, relevant State constitutions, the Maxims of Equity, and enduring principles reflected in Magna Carta (1215) and due process.
I do not seek to evade any lawful obligation. My intent is to:
Stand in honor,
Settle and close any lawfully verified obligation, and
Ensure performance is lawful, constitutional, and equitable, consistent with my Covenant with the Constitution.

2. Conditional Acceptance (Non-Dispute)
Please take formal Notice that I do not refuse your presentments or demands. Instead:
I conditionally accept your Offer and/or demand to pay and settle the alleged tax liability in full, upon your strict proof of claim and upon your written clarification as to how you wish me to lawfully pay in accordance with Article I, Section 10, Clause 1 of the Constitution and other applicable law.
This Conditional Acceptance is expressly made on lawful and reasonable conditions, set forth below. Upon your complete and truthful compliance, I am prepared to perform in honor.

3. Lawful Payment Inquiry – Article I, Section 10, Clause 1
Article I, Section 10, Clause 1 provides in relevant part: "No State shall… make any Thing but gold and silver Coin a Tender in Payment of Debts."
While I recognize that tax statutes and codes rely on various forms of "legal tender" and accounting units, I remain obligated, in conscience and covenant, to honor the supreme law as I understand it. To avoid any dishonor, confusion, or unlawful conversion, I require clarity on the following:

Form of Lawful Tender You Require
a. Please state with specificity what you recognize and accept as lawful payment / lawful money acceptable for discharging the alleged tax liability, consistent with Article I, Section 10, Clause 1.
b. If you insist upon payment solely in Federal Reserve Notes or bank credit, please explain how such instruments satisfy, supplement, or lawfully supersede the requirement of "gold and silver Coin" as tender in payment of debts.

Statutory & Constitutional Basis
a. Identify all statutes, regulations, and legal authorities you rely upon to treat Federal Reserve Notes or other instruments as lawful tender for tax obligations, and explain how they coexist or reconcile with Article I, Section 10, Clause 1.
b. If you rely on federal or state emergency measures, monetary legislation, or case law, please provide specific citations and explain their constitutional foundation.

Specific Lawful Payment Instructions
a. Provide precise, step-by-step instructions for how I may lawfully tender payment in a form that satisfies both your procedures and the Constitution as the supreme law, including any available procedure for: Payment in lawful money, or Redemption, offset, or discharge mechanisms recognized by your agency.
b. If you maintain that no alternative to Federal Reserve Notes or bank credit is available, please say so clearly and under oath, and explain how that policy complies with the constitutional tender clause.

Upon receiving your full, verified responses, I am prepared to tender lawful payment or equivalent lawful performance in a way that:
Keeps me in honor,
Does not require violation of my Covenant with the Constitution, and
Is consistent with principles of equity and due process.

4. Proof of Claim & Verification of the Alleged Tax Liability
As part of this Conditional Acceptance and in the spirit of transparency, equity, and due process, kindly provide the following, each sworn true, correct, and complete under penalty of perjury by an authorized officer with personal knowledge:

Nature and Source of Liability
A written explanation of the exact statutory basis for the alleged tax liability, including:
The specific Subtitle, Chapter, Subchapter, and Section(s) of the Internal Revenue Code or State Tax Code that you claim applies to me,
The classification you assert (e.g., "taxpayer," "employer," "withholding agent," "fiduciary," "resident/non-resident," "person liable," etc.), and
How and when I allegedly elected, consented, or became lawfully obligated in that status.

Assessment Documentation
A copy of the signed, dated assessment records, including:
Name and title of the assessment officer,
Date(s) of assessment,
Exact amount(s) assessed (tax, interest, penalties),
Evidence that the assessment was made in accordance with law and issued to the correct party.

Delegation & Authority
Documentation showing the lawful delegation of authority from the sovereign taxing power to your agency (especially if you are a private collector or contracted entity). Identification of the specific delegation order or statutory provision authorizing you or your office to collect, compromise, or enforce the alleged tax in my specific case and jurisdiction.

Accounting & Adjustments
A full and complete accounting year-by-year, including:
Original alleged tax due,
Credits, offsets, and withholdings already applied,
Penalties and interest computations, with formulas and statutory basis,
Any refunds, abatements, or prior adjustments.
Confirmation as to whether the alleged liability has been sold, securitized, offset, insured, or otherwise satisfied by any third party, so that there is no double recovery.

Notice & Opportunity to Be Heard
Proof that I have been provided all notices, hearings, and procedural safeguards required by law before any levy, lien, garnishment, or other enforcement action, including:
Dates and copies of each critical notice,
Evidence of proper mailing and/or service,
Notice of rights to appeal, challenge, or contest assessments before an impartial tribunal.

Sworn Verification
A sworn affidavit by an officer with personal knowledge that:
The alleged tax liability is lawfully due and unpaid,
All statutory and constitutional requirements have been met,
Your agency's records are complete, accurate, and not misleading,
No other party has a superior claim to the same alleged liability.

This is fully consistent with equity, natural justice, and the spirit of Magna Carta, which prohibits arbitrary exactions and deprivations of property without due process and lawful judgment.

5. Equity, Magna Carta & Constitutional Covenant
I stand upon the following Maxims of Equity and principles:
Equity will not suffer a wrong to be without a remedy.
He who seeks equity must do equity.
He who comes into equity must come with clean hands.
Equity follows the law, not subverts it.
Equity looks to the intent rather than the form.

I also recognize the enduring principles reflected in Magna Carta, including:
Protection from unlawful or arbitrary taxation / exaction,
The right not to be deprived of property except by lawful judgment and due process,
The expectation that government agents act within the bounds of their lawful authority and oath.

I am willing to do equity and settle any lawful, verified tax obligation, provided that:
Your claim is made in good faith,
Your authority and procedures are lawful and constitutional, and
The method of payment does not require me to step outside my Covenant with the Constitution.

6. Time to Respond & Effect of Non-Response
You are hereby afforded twenty-one (21) days from the date you receive this Notice (as confirmed by Certified Mail records) to provide full, complete, and responsive answers to every point raised, together with the requested documentation and sworn verifications.
Failure to respond in good faith and with particularity, or providing only non-responsive form letters, shall be taken as:
Your refusal or inability to verify the alleged tax liability as a lawful, collectible obligation, and/or to demonstrate your authority and jurisdiction in this matter;
Your refusal or inability to clearly identify a constitutionally compliant form of tender for payment;
Your tacit admission, by acquiescence, that:
You lack verifiable, lawful standing to enforce the alleged claim against me as presented, and/or
You are unwilling or unable to state how I may lawfully pay in a manner consistent with Article I, Section 10, Clause 1 and the Constitution as the supreme law;
Your waiver of any claim that I have refused to pay or acted in dishonor, since I have clearly offered conditional acceptance and a willingness to settle in honor upon proof of claim and lawful payment instructions.

Nothing in this Notice shall be construed as an admission of liability, a waiver of any defenses, or a consent to jurisdiction not otherwise established by law.

7. Reservation of Rights
I expressly reserve all unalienable rights and remedies, including but not limited to:
Rights secured by the Constitution for the United States of America and the applicable State Constitution,
Rights at common law,
Rights and remedies in equity,
Rights to due process, fair hearing, and full disclosure,
Rights to challenge jurisdiction, standing, and validity of any claim, assessment, lien, or levy.

This Notice is issued without prejudice, with clean hands, in peace and honor, and shall not be construed as a waiver of any right, remedy, or defense at law or in equity.

Sincerely,
${clientName}, in Sui Juris
All Rights Reserved, Without Prejudice

Certificate of Service
I, ${clientName}, certify that a true and correct copy of the foregoing Notice of Conditional Acceptance (Non-Dispute; Lawful Payment Inquiry – Tax Collection Agency) was sent via Certified Mail No. [__________] to: ${opposingParty} on this [____] day of [Month], [Year].
${clientName}`;
    } else {
      // Consumer Contract template
      return `Notice of Conditional Acceptance (Non-Dispute; Lawful Payment Inquiry)

${clientName}, in Sui Juris
c/o ${clientAddress}
${clientCity}, ${clientState} ${clientZip}
${formattedDate}
Via Certified Mail No.: [_________]

To:
${opposingParty}
${opposingAddress}

Re: Notice of Conditional Acceptance / Lawful Payment Inquiry
Reference: Your Alleged "Debt" / "Account" No. [ ], Dated [ ]

1. Introduction & Status

I, ${clientName}, a living man/woman, standing in peace and honor, issue this Notice of Conditional Acceptance (Non-Dispute; Lawful Payment Inquiry) in response to your presentment styled as: "[Title of Letter]" dated [date of their letter], referencing "Account No." [___________]. This Notice is made without prejudice, reserving all rights at law and in equity, including but not limited to Article I, Section 10, Clause 1 of the Constitution for the United States of America, the Maxims of Equity, and the enduring protections reflected in Magna Carta (1215) and subsequent due process principles. I seek to settle and close any lawful, verifiable obligation in honor, upon proof of claim and upon your clear instructions as to how such obligation can be lawfully discharged consistent with the Constitution and applicable public policy.

2. Conditional Acceptance (Non-Dispute)

Please take formal Notice that I do not refuse your Offer or presentment. Instead, I hereby state: I conditionally accept your Offer to pay and/or settle the alleged account in full, upon your strict proof of claim and upon your written clarification of how you wish me to lawfully pay in compliance with Article I, Section 10, Clause 1 of the Constitution and other controlling law. This Conditional Acceptance is expressly made on the following conditions, which are lawful, reasonable, and necessary for me to proceed in honor and good faith.

3. Lawful Payment Inquiry – Article I, Section 10, Clause 1

Article I, Section 10, Clause 1 of the Constitution for the United States of America provides in pertinent part that: "No State shall… make any Thing but gold and silver Coin a Tender in Payment of Debts." In light of this constitutional mandate, and in order to avoid dishonor and confusion, kindly provide sworn, written answers to the following:

Form of Lawful Money
a. Please state exactly what you recognize and accept as lawful payment / lawful money for purposes of discharging this alleged obligation, consistent with Article I, Section 10, Clause 1.
b. If you accept only bank credit or Federal Reserve Notes, please explain how such instruments constitute "gold and silver Coin" or lawful tender within the meaning of the Constitution.

Specific Lawful Payment Instructions
a. Please provide detailed, step-by-step instructions on how I may lawfully tender payment in a method that is fully compliant with the Constitution, including any lawful money redemption procedures, if applicable.
b. If you rely on federal or state statutes to define acceptable tender different from or in addition to "gold and silver Coin," please identify each statute and explain how it reconciles with Article I, Section 10, Clause 1.

Authority & Capacity
a. Please identify, under oath, the name, title, and authority of the officer or agent who determined that your demanded form of payment is constitutional and lawful.
b. Please provide evidence that your entity is licensed, bonded, and authorized to collect this alleged debt in my state, and that you are not violating any fair debt collection or consumer protection laws.

Upon your full, complete, and verified answers, I am prepared, in honor, to perform and to tender lawful payment in a manner that:
Does not place me in dishonor,
Does not require me to violate my Covenant with the Constitution, and
Is consistent with the Maxims of Equity and due process.

4. Proof of Claim & Verification of the Alleged Debt

Further, as part of this Conditional Acceptance and in the spirit of equity and fair dealing, kindly provide the following documents and verifications, each sworn to be true, correct, and complete under penalty of perjury by an officer with personal knowledge:

Original Contract / Instrument
A true, complete, and certified copy of the original, signed contract or agreement bearing my wet-ink signature that allegedly created this obligation, showing full disclosure of terms, creditor, consideration, and risk.

Chain of Title / Assignment
All assignments, transfers, or sales of the alleged account or instrument, including bills of sale, with proof that you have full, present, lawful, and beneficial interest as creditor, not merely a servicer or third-party debt buyer without the original instrument.

Accounting & Consideration
A full ledger / accounting of how the alleged balance was computed, including principal, interest, fees, and charges, from inception to present. Evidence of lawful consideration given by the original creditor: that actual, lawful value was extended and that no double-bookkeeping, insurance payoff, securitization, or charge-off has already satisfied or discharged the account.

Verification Under Oath
A sworn affidavit by an officer with personal knowledge that:
a. The alleged debt is valid, due, and unpaid;
b. Your entity holds lawful title and right to enforce;
c. No other party has superior or competing claim to the same account or instrument.

This request is consistent with equity and with fundamental principles of natural justice and due process, as well as the spirit of Magna Carta, which requires that no person be deprived of property without lawful judgment or due process of law.

5. Equity, Magna Carta & Constitutional Covenant

I stand on the following maxims and principles:
Equity will not suffer a wrong to be without a remedy.
He who seeks equity must do equity.
He who comes into equity must come with clean hands.
Equity acts in personam, not in rem.
Equity follows the law —including the Constitution as the supreme law to which all officers are bound by oath.

In addition, the spirit of Magna Carta (1215) and its progeny enshrines:
The right not to be deprived of property but by lawful judgment and due process,
Protection against arbitrary exactions and unlawful takings.

Accordingly, I am willing to do what is just and equitable; I am merely seeking clarity, verification, and lawful means of performance so that any settlement is:
In honor,
In law, and
Consistent with my Covenant with the Constitution.

6. Time to Respond & Effect of Non-Response

You are hereby given twenty-one (21) days from the date you receive this Notice (as shown by Certified Mail records) to provide full, complete, and responsive answers to each and every point raised herein, with supporting documentation and sworn verification. Failure to respond in good faith and with particularity, or sending a non-responsive form letter, shall be deemed:

Your refusal or inability to verify the alleged debt and to show me how to lawfully pay in accordance with the Constitution;
Your tacit admission, by acquiescence, that:
You have no verifiable lawful claim against me, and/or
You are unwilling or unable to identify a constitutionally compliant form of tender for payment;
Your waiver of any claim that I have acted in dishonor or refusal, since I have clearly offered conditional acceptance upon proof of claim and lawful payment instructions.

Nothing in this Notice shall be construed as an admission of liability, an agreement to any alleged balance, or a waiver of any rights, remedies, or defenses at law or in equity.

7. Reservation of Rights

I expressly reserve all unalienable and secured rights, including but not limited to:
Rights secured by the Constitution for the United States of America,
Rights under applicable State constitutions and common law,
Rights and remedies in equity and lawful due process,
The right to challenge jurisdiction, standing, and validity of any alleged claim.

This Notice is made without prejudice, with clean hands, in peace and honor.

Sincerely,

${clientName}, in Sui Juris
All Rights Reserved, Without Prejudice

Certificate of Service

I, ${clientName}, certify that a true and correct copy of the foregoing Notice of Conditional Acceptance (Non-Dispute; Lawful Payment Inquiry) was sent via Certified Mail No. [__________] to: ${opposingParty} on this [____] day of [Month], [Year].

${clientName}`;
    }
  };

  // Notice of Discharge Final Template for Debt Discharge Second Mailing
  const getDebtDischargeNoticeDischargeFinalTemplate = () => {
    const clientName = clientInfo.fullName || '[Your Full Name]';
    const clientAddress = clientInfo.streetAddress || '[Your Mailing Location]';
    const clientCity = clientInfo.city || '[City]';
    const clientState = clientInfo.state || '[State]';
    const clientZip = clientInfo.zipCode || '[ZIP]';
    const opposingParty = opposingPartyInfo.fullName || '[Name of Debt Collector / Agency]';
    const opposingAddress = opposingPartyInfo.fullAddress || '[Address]';
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'long', 
      year: 'numeric' 
    });

    return `Notice of Discharge (Post‑Conditional Acceptance; Equity & English Common Law)

${clientName}, in Sui Juris
c/o ${clientAddress}
${clientCity}, ${clientState} ${clientZip}
${formattedDate}
Via Certified Mail No.: [_________]

To:
${opposingParty}
${opposingAddress}

Re: Notice of Discharge & Closure of Alleged Account
Previous: Notice of Conditional Acceptance / Lawful Payment Inquiry dated [__________]
Your Alleged Account / Reference No.: [___________]

1. Notice of Prior Conditional Acceptance

I, ${clientName}, a living man/woman, standing in peace and honor, refer you to my prior Notice of Conditional Acceptance (Non-Dispute; Lawful Payment Inquiry) dated [insert date of your previous notice], sent to you via Certified Mail No. [_________], wherein I:

Did not refuse your presentment, but Conditionally accepted your alleged claim upon proof of claim, full verification, and clear lawful payment instructions consistent with: English Common Law and Equity Jurisprudence, Maxims of Equity, The Magna Carta (1215) in its enduring principles, and My Covenant with the Constitution and due process.

In that prior Notice, I provided you with a reasonable time (e.g., twenty-one (21) days) to respond with:
* Verified proof of a lawful, enforceable obligation;
* Evidence of your standing and authority;
* A full accounting of the alleged debt; and
* Clear instructions as to how I may lawfully tender payment without violating higher law or equity.

2. Record of Your Default / Non-Performance

As of the date of this Notice:

* You have failed to provide full, complete, and responsive answers to the conditions and lawful inquiries set forth in my Conditional Acceptance; and/or
* Your communication(s), if any, have been non-responsive, evasive, or unsigned, and have not cured the defects identified; and/or
* You have refused or neglected to produce any sworn verification of:
    * A valid contract with my wet-ink signature,
    * Your lawful standing as true creditor, and
    * A constitutionally compliant method of lawful payment.

Accordingly, under Equity and English Common Law, your silence, non-performance, or defective performance operates as a form of tacit admission and default, consistent with the following principles:
* Equity will not suffer a wrong to be without a remedy.
* He who seeks equity must do equity.
* Silence, where there is a duty to speak, may operate as acquiescence.
* He who comes into equity must come with clean hands.

Having been given fair opportunity to substantiate your claim, you have failed or refused to do so. Standing in equity, I cannot be compelled to perform upon a claim that is unverified, defective, or unsupported by lawful evidence.

3. Notice of Discharge & Closure of Alleged Account (In Equity)

Therefore, in reliance upon:

* Your default by non-response or non-performance,
* The absence of verified proof of claim,
* The Maxims of Equity,
* Principles embodied in Magna Carta, including protection from arbitrary exactions and deprivation of property without lawful judgment and due process, and
* My right to settle matters in honor under English Common Law and Equity Jurisprudence,

I hereby provide formal Notice that:

The alleged account / claim identified as [Account / Reference No. ________], together with any and all derivative claims, is deemed discharged, settled, and closed in equity, with no lawful, verified obligation remaining due from me to you.

This Notice of Discharge does not purport to "erase" any entry you may have made in your own private records; rather, it:
* Records that, as between yourself and me, you have failed to prove a lawful claim;
* Records that I have stood in honor, given opportunity for proof, and conditionally accepted upon lawful terms;
* Records that any further attempts to collect or enforce this alleged debt against me, absent proper lawful judgment and proof, may constitute:
    * Harassment,
    * Misrepresentation,
    * Unfair or deceptive practices, and
    * A violation of duties owed under common law and equity.

4. Cease & Desist from Further Collection Activity

In light of the above, you are hereby instructed to:

Cease and desist from any further attempts to collect on the alleged account [No. ________] as against me, including but not limited to:
* Telephone calls, text messages, or emails,
* Collection letters or threatening notices,
* Third-party communications,
* Any action reasonably calculated to harass, intimidate, or coerce payment of an unverified claim.

Cease and desist from reporting or publishing any adverse information regarding this alleged account to third parties, including credit reporting agencies, under the pretense that the alleged obligation is valid, unpaid, or undisputed, when in fact:
* It has been lawfully challenged,
* You have failed to verify the obligation, and
* It is deemed discharged and closed in equity.

If you believe you still hold a lawful claim despite your default, you are required to immediately provide:
* A sworn affidavit from an officer with full personal knowledge rebutting the default and proving your claim; and
* Full documentation curing all prior defects set out in my Notice of Conditional Acceptance.

In the absence of such sworn, particularized rebuttal, this Notice of Discharge shall stand unrebutted.

5. English Common Law, Equity & Magna Carta Principles

For clarity of record, my position is grounded in the following legal and equitable maxims and principles (non-exhaustive):

* No man shall be condemned or dispossessed except by lawful judgment and due process (reflected in Magna Carta clauses 39–40).
* Equity looks to the intent rather than the form —my intent has always been to settle in honor, not to evade.
* Equity follows the law —where statutory or commercial practice conflicts with higher law and basic justice, equity demands clarity, proof, and clean hands.
* Where there is a conflict between equal equities, the first in time shall prevail —my prior Conditional Acceptance and lawful inquiries, unanswered, stand first in time and unrebutted in substance.
* He who alleges must prove —you, as the alleged creditor or assignee, bear the burden of proof to show:
    * A lawful, binding contract,
    * Your full right, title, and interest in the alleged obligation, and
    * A lawful method of payment that keeps me in honor under higher law.

Having been afforded the chance to prove your position and to "do equity," your failure to do so is fatal in equity to the enforcement of your claim.

6. Notice of Liability for Further Unlawful Actions

Take Notice that any continued attempts to:

* Pursue collection on this discharged and unverified alleged debt,
* Report the alleged debt as valid and unpaid to third parties, or
* Initiate legal proceedings without first curing the defaults identified herein,

may subject you and your agents to personal and vicarious liability under:
* Common law tort principles (e.g., harassment, defamation, misrepresentation, negligent or fraudulent misstatement),
* Applicable consumer protection and fair collection laws, and
* General principles of Equity, including potential claims for appropriate equitable relief.

Nothing in this Notice is to be construed as consent to any jurisdiction or as a waiver of any rights, claims, or defenses. I explicitly reserve the right to reference your default and this Notice of Discharge in any future administrative or judicial proceedings, if necessary.

7. Reservation of Rights

I hereby expressly reserve all rights:
* At English Common Law,
* In Equity Jurisprudence,
* Under the Constitution and applicable bills of rights,
* Under the Maxims of Equity and principles reflected in Magna Carta, and
* Under any other applicable law or remedy.

This Notice is issued without prejudice, in peace and honor, with clean hands.

Sincerely,

${clientName}, in Sui Juris
All Rights Reserved, Without Prejudice

Certificate of Service

I, ${clientName}, certify that a true and correct copy of the foregoing Notice of Discharge & Closure of Alleged Account was sent via Certified Mail No. [__________] to: ${opposingParty} on this [____] day of [Month], [Year].

${clientName}`;
  };

  // Use appropriate categories based on case type
  const legalCategories = caseType === 'petitioner' ? petitionerCategories : 
                         caseType === 'debt_discharge' ? debtDischargeCategories : 
                         defendantCategories;

  const saveLegalMatter = async (remedyData) => {
    try {
      // Create a title based on category and timestamp
      const categoryLabel = legalCategories.find(cat => cat.value === selectedCategory)?.label || 'Legal Matter';
      const title = `${categoryLabel} Matter - ${new Date().toLocaleDateString()}`;

      const legalMatterData = {
        conversation_id: conversationId,
        title: title,
        category: selectedCategory,
        case_description: caseDescription,
        client_info: clientInfo,
        opposing_party_info: opposingPartyInfo,
        remedy_content: remedyData.fullResponse,
        uploaded_documents: uploadedDocuments.map(doc => ({
          name: doc.name,
          size: doc.size,
          type: doc.type,
          uploadedAt: doc.uploadedAt
        })),
        generated_documents: generatedDocuments,
        generated_tab3_documents: generatedTab3Documents
      };

      // Save to legal matters database
      const response = await axios.post(
        `${API_BASE}/legal-matters`,
        legalMatterData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Also update conversation metadata to preserve data when switching - include ALL data
      const conversationUpdate = {
        metadata: {
          case_type: caseType, // Save case type as permanent for this legal matter
          category: selectedCategory,
          case_description: caseDescription,
          client_info: clientInfo,
          opposing_party_info: opposingPartyInfo,
          uploaded_documents: uploadedDocuments,
          generated_documents: generatedDocuments,
          generated_tab3_documents: generatedTab3Documents,
          remedy_content: remedyData.fullResponse,
          last_remedy_generated: new Date().toISOString(),
          has_remedy_data: true  // Flag to indicate this conversation has meaningful content
        }
      };

      await axios.put(
        `${API_BASE}/conversations/${conversationId}`,
        conversationUpdate,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data) {
        console.log('Legal matter saved successfully with full data preservation:', response.data);
      }
    } catch (error) {
      console.error('Failed to save legal matter:', error);
      // Don't show error toast to user since this is auto-save
    }
  };

  const generatePetitionPrompt = () => {
    return `You are to act as a Common Law Barrister and Petition Writer. Draft a Petition based on the legal category provided: ${selectedCategory.replace('-', ' ').toUpperCase()}. The Petition must:

Frame the Matter – Clearly outline the petitioner's issue and the opposing party's actions, referencing the selected legal category.

Establish Jurisdiction – Assert the authority of English Common Law, the Magna Carta (1215), the English Bill of Rights (1689), and the U.S. Bill of Rights, noting the supremacy of these charters as higher law above statutory codes.

Apply Equitable Jurisprudence – Use the Maxims of Equity to demonstrate fairness, justice, and conscience. Show why Equity must intervene when law alone is insufficient.

Assert Rights – Highlight the petitioner's natural, constitutional, and equitable rights that are being infringed.

Conditional Acceptance – Where appropriate, include language that conditionally accepts claims by the opposing party, provided they can prove their standing, jurisdiction, and lawful authority.

Relief Requested – Conclude with a clear demand for remedy, restoration, or discharge, ensuring the petition is respectful, honorable, and lawful.

Format – Use solemn, affidavit-style language with headings, numbered points, and references to foundational law. The tone must be authoritative, respectful, and unwavering.

Case Details:
- Petitioner: ${clientInfo.fullName || user?.full_name || '[Your Name]'}
- Category: ${selectedCategory.replace('-', ' ').toUpperCase()}
- Matter Description: ${caseDescription}
- Opposing Party: ${opposingPartyInfo.fullName || '[Opposing Party]'}`;
  };

  const handleGenerateRemedy = async () => {
    if (!selectedCategory || !caseDescription.trim() || !clientInfo.country) {
      toast({
        title: "Missing Information",
        description: "Please select a country, category, and provide a case description.",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Use different prompts based on case type
      let prompt;
      if (caseType === 'petitioner') {
        prompt = generatePetitionPrompt();
      } else if (caseType === 'debt_discharge') {
        const debtType = selectedCategory === 'tax-collection' ? 'tax collection agency' : 'consumer debt collector';
        prompt = `You are a Common Law Barrister analyzing a DEBT DISCHARGE matter against a ${debtType}. Provide a comprehensive Common Law Barrister analysis with exactly THREE SECTIONS:

SECTION 1: DEBT DISCHARGE OVERVIEW
- Analyze the alleged ${selectedCategory === 'tax-collection' ? 'tax liability' : 'consumer debt'} under Common Law, Equity, and Constitutional principles
- Explain why conditional acceptance (non-dispute) is the lawful approach
- Reference Article I, Section 10, Clause 1 regarding lawful tender requirements
- Detail the Maxims of Equity applicable to debt discharge
- Reference Magna Carta protections against arbitrary exactions
- Explain the lawful payment inquiry process
- Emphasize standing in honor, not refusal

SECTION 2: ENFORCING YOUR DEBT DISCHARGE REMEDY
- Recognition of the presentment/claim
- Affidavit of Status establishing your standing as a living man/woman
- Testimony Under God with solemn declaration
- Conditional Acceptance upon strict proof of claim
- Demand for verification of the alleged ${selectedCategory === 'tax-collection' ? 'tax liability' : 'debt'}
- Article I Section 10 Clause 1 lawful money inquiry
- Document production requirements for the ${debtType}

SECTION 3: THE DEBT DISCHARGE ADMINISTRATIVE PROCESS
- Step-by-step process for ${selectedCategory === 'tax-collection' ? 'tax liability' : 'consumer debt'} discharge
- Explain what happens if they fail to respond (default)
- Detail the 21-day response window
- Explain the tacit admission principle (silence = acquiescence)
- Cover reservation of rights
- Close with remedy in honor through conditional acceptance

Case Description: ${caseDescription}

CRITICAL: Frame this as conditional acceptance (NOT refusal). The user is willing to settle any lawful, verified obligation in honor, but requires proof of claim and lawful payment instructions.`;
      } else {
        prompt = `You are a Common Law Barrister analyzing a ${selectedCategory} matter. Provide a comprehensive Common Law Barrister analysis with exactly THREE SECTIONS:

SECTION 1: LEGAL MATTER OVERVIEW
- State the matter and analyze under Common Law, Equity, and Constitutional principles
- Identify jurisdiction & standing (Common Law vs corporate fiction)
- Detail English Common Law violations (no harm/no crime, due process, property rights, etc.)
- Highlight Equity Jurisprudence violations (Maxims of Equity breaches)
- Reference Magna Carta & English Bill of Rights (1689) protections
- Show U.S. Constitutional parallels (Article I Section 10, 4th/5th/6th/7th Amendments)
- Provide final overview with remedy in honor

SECTION 2: ENFORCE YOUR EQUITABLE RIGHTS
- Recognition of the matter presented
- Affidavit of Status for living man/woman (not legal fiction)
- Testimony Under God with solemn declaration
- Conditional Acceptance upon proof of claim with full accounting
- Article I Section 10 Clause 1 assertion ("No State shall make any Thing but gold and silver Coin a Tender in Payment of Debts")
- Emphasize lawful payment & equity (since 1933 no lawful medium, discharge only)
- Document production for service with settlement in honor

SECTION 3: EXECUTING THE ADMINISTRATIVE PROCESS  
- Explain Article I Section 10 Clause 1 (gold/silver requirement, equitable discharge)
- Detail Administrative Process purpose (private dispute resolution)
- 5-step process: (1)Notice of Conditional Acceptance, (2)Second Notice/Opportunity to Cure, (3)Notice of Default, (4)Certificate of Non-Response, (5)Remedy & Enforcement
- Explain administrative default importance (burden shift, enforceable record)
- Close with remedy in honor (peaceful settlement via Conditional Acceptance)

CRITICAL: All fee schedules must specify payment ONLY in lawful money (constitutional gold and silver coin) with explicit rejection of Federal Reserve Notes.

Generate all three sections with detailed analysis for each requirement.`;
      }

      // Build uploaded document context for AI analysis
      let documentContext = '';
      const docsWithText = uploadedDocuments.filter(doc => doc.extractedText);
      if (docsWithText.length > 0) {
        documentContext = '\n\n--- UPLOADED DOCUMENTS FOR ANALYSIS ---\nThe user has uploaded the following document(s). Incorporate the content, claims, allegations, demands, and deadlines found in these documents directly into your legal analysis and remedy recommendations:\n';
        for (const doc of docsWithText) {
          let text = doc.extractedText;
          if (text.length > 30000) text = text.substring(0, 30000) + '\n[Document truncated due to length]';
          documentContext += `\n--- Document: ${doc.name} ---\n${text}\n`;
        }
        documentContext += '\n--- END OF UPLOADED DOCUMENTS ---\n\nCRITICAL: Your analysis MUST reference specific content from the uploaded documents above. Identify claims, presumptions, deadlines, jurisdictional assertions, and procedural defects found in these documents and address them directly in your remedy strategy.\n';
      }

      // Include uploaded documents in the request if any
      const requestData = {
        category: selectedCategory,
        caseDescription: caseDescription.trim(),
        prompt: prompt + documentContext,
        country: clientInfo.country, // NEW: Pass country to backend
        uploadedDocuments: uploadedDocuments.length > 0 ? uploadedDocuments.map(doc => ({
          name: doc.name,
          size: doc.size,
          type: doc.type,
          uploadedAt: doc.uploadedAt,
          hasExtractedText: !!doc.extractedText
        })) : []
      };

      // Use the new comprehensive remedy endpoint for detailed legal guidance
      const response = await axios.post(
        `${API_BASE}/conversations/${conversationId}/generate-remedy`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        // Create structured remedy object for display
        const fullResponse = response.data.remedy;
        
        // Debug: Log the full response to see its structure
        console.log("Full OpenAI Response:", fullResponse);
        
        const remedyData = {
          category: selectedCategory,
          fullResponse: fullResponse,
          timestamp: new Date().toISOString(),
          conversationId: conversationId,
          uploadedDocuments: uploadedDocuments
        };

        setRemedy(remedyData);

        // Auto-save legal matter to database
        await saveLegalMatter(remedyData);

        // Auto-generate documents based on case type
        if (caseType === 'debt_discharge') {
          // For Debt Discharge - auto-generate the First Mailing documents
          await autoGenerateDebtDischargeDocuments();
          
          // Also auto-generate Tab 3 documents (Second Mailing - Certificate of Non-Response, etc.)
          await autoGenerateTab3Documents();
        } else {
          // Auto-generate Tab 2 documents (Equitable Rights documents)
          await autoGenerateTab2Documents();

          // Auto-generate Tab 3 documents (Administrative Process documents)  
          await autoGenerateTab3Documents();
        }

        toast({
          title: "Legal Remedy Generated",
          description: caseType === 'debt_discharge' 
            ? "Debt discharge remedy and documents have been generated successfully."
            : "Comprehensive three-section administrative process guidance has been created and saved with all legal documents auto-generated.",
        });
      } else {
        throw new Error("Failed to generate remedy");
      }
    } catch (error) {
      console.error('Failed to generate remedy:', error);
      
      // Provide fallback remedy with comprehensive guidance
      const fallbackRemedy = createFallbackRemedy(selectedCategory, caseDescription);
      setRemedy(fallbackRemedy);
      
      toast({
        title: "Remedy Generated (Offline Mode)",
        description: "Generated using fallback legal guidance template.",
        variant: "default"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Helper function to extract sections from the comprehensive response
  const extractSection = (fullText, sectionTitle) => {
    if (!fullText || typeof fullText !== 'string') {
      return `No content available for ${sectionTitle.toLowerCase()}.`;
    }
    
    try {
      // Try multiple patterns to find sections
      const patterns = [
        new RegExp(`${sectionTitle}([\\s\\S]*?)(?=SECTION|$)`, 'i'),
        new RegExp(`## ${sectionTitle}([\\s\\S]*?)(?=##|$)`, 'i'),
        new RegExp(`# ${sectionTitle}([\\s\\S]*?)(?=#|$)`, 'i'), 
        new RegExp(`${sectionTitle}:([\\s\\S]*?)(?=\\n\\n|$)`, 'i'),
        new RegExp(`\\*\\*${sectionTitle}\\*\\*([\\s\\S]*?)(?=\\*\\*|$)`, 'i')
      ];
      
      for (const pattern of patterns) {
        const match = fullText.match(pattern);
        if (match && match[1] && match[1].trim().length > 10) {
          return match[1].trim();
        }
      }
      
      // If no sections found but looking for SECTION 1, return a portion of the full text
      if (sectionTitle === 'SECTION 1') {
        return fullText.substring(0, 1000) + (fullText.length > 1000 ? "..." : "");
      }
      
      return `Content for ${sectionTitle} will appear here when generated.`;
      
    } catch (error) {
      console.error('Error extracting section:', error);
      return `Error loading ${sectionTitle} content.`;
    }
  };

  const highlightUserFields = (text) => {
    if (!text) return text;
    
    // Replace [[double bracket]] markers with highlighted spans
    let highlightedText = text.replace(/\[\[([^\]]+)\]\]/g, (match, field) => {
      return `<mark class="bg-yellow-200 text-yellow-900 font-semibold px-1 rounded">${field}</mark>`;
    });
    
    // Also replace [single bracket] markers with highlighted spans
    highlightedText = highlightedText.replace(/\[([^\]]+)\]/g, (match, field) => {
      return `<mark class="bg-yellow-200 text-yellow-900 font-semibold px-1 rounded">${field}</mark>`;
    });
    
    return highlightedText;
  };

  const renderRemedyWithHighlights = (text) => {
    if (!text) return 'Content will appear here...';
    
    // First highlight user fields
    const highlightedText = highlightUserFields(text);
    
    // Return as HTML (using dangerouslySetInnerHTML)
    return { __html: highlightedText };
  };

  // Create comprehensive fallback remedy following three-section framework
  const createFallbackRemedy = (category, description) => {
    const fallbackContent = `
# SECTION 1: LEGAL MATTER OVERVIEW

## Step 1: State the Matter
**Legal Category**: ${category.toUpperCase()}
**Case Description**: ${description}

## Step 2: Jurisdiction & Standing Analysis
- **Jurisdiction**: This ${category} matter falls under Common Law and Equity jurisdiction
- **Standing**: You are a living man/woman with inherent rights, not a corporate fiction

## Step 3: English Common Law Violations
- **No harm, no loss, no injury = no crime**: Examining actual harm vs. statutory presumptions
- **Due process of law**: Constitutional procedural requirements not being met
- **Right to property and estate**: Fundamental property protections potentially violated
- **Presumption of innocence**: Burden of proof improperly shifted

## Step 4: Equity Jurisprudence Violations
- **"Equity will not suffer a wrong without a remedy"**: You are entitled to equitable relief
- **"Equity follows the law"**: Constitutional law must supersede statutory presumptions
- **"He who seeks equity must do equity"**: All parties must act fairly and in good faith

## Step 5: Historical Anchors
- **Magna Carta Clause 39**: "No free man shall be taken or imprisoned... except by lawful judgment of his peers"
- **English Bill of Rights (1689)**: Protection against excessive fines without due process

## Step 6: U.S. Constitutional Parallels
- **Article I Section 10**: "No State shall make any Thing but gold and silver Coin a Tender in Payment of Debts"
- **4th Amendment**: Protection against unreasonable searches and seizures
- **5th Amendment**: Due process clause and protection against self-incrimination

## Step 7: Final Overview
**Violations**: Statutory presumption without proof of harm, due process failures, constitutional violations
**Remedy in Honor**: Conditional acceptance with demand for proof of claim and lawful authority

---

# SECTION 2: ENFORCE YOUR EQUITABLE RIGHTS

## Step 1: Recognition
I acknowledge the ${category} matter presented for honorable resolution under Common Law and Equity principles.

## Step 2: Status Affirmation - Affidavit of Status
**I, _____________ [Your Full Name], am a living man/woman, not a corporate fiction or statutory entity.**

## Step 3: Testimony Under God
**TESTIMONY UNDER GOD**
Under God, I declare and testify that I am a living soul with unalienable rights. I invoke Article I Section 10 of the Constitution and the Maxims of Equity in addressing this matter with clean hands and in honor.

## Step 4: Conditional Acceptance
I conditionally accept this ${category} matter for value, upon proof of:
1. **Constitutional Authority**: Evidence of lawful authority under constitutional constraints
2. **Personal Injury**: Proof of actual harm to a living man or woman
3. **Lawful Contract**: Verified contract establishing jurisdiction over my person
4. **Full Accounting**: Complete accounting in lawful money (constitutional gold and silver coin)

## Step 5: Article I Section 10 Clause 1 Assertion
**"No State shall make any Thing but gold and silver Coin a Tender in Payment of Debts"**

Since 1933, there is no lawful medium of exchange. All obligations must be settled in constitutional money (gold/silver coin) or discharged equitably.

**Federal Reserve Notes are NOT lawful money and are rejected.**

## Step 6: Document Production
**NOTICE OF CONDITIONAL ACCEPTANCE** - Formatted for service with constitutional protections and lawful money requirements.

---

# SECTION 3: EXECUTING THE ADMINISTRATIVE PROCESS

## Step 1: Explain Article I Section 10 Clause 1
"No State shall make any Thing but gold and silver Coin a Tender in Payment of Debts"
Since constitutional money is not in circulation, all commercial obligations must be settled through equitable discharge.

## Step 2: Administrative Process Purpose
The Administrative Process provides lawful, private dispute resolution before court escalation. 
Conditional Acceptance = "Yes, I accept - upon proof of your claim and authority."

## Step 3: 5-Step Administrative Process
**STEP 1 (Day 1)**: Notice of Conditional Acceptance - Accept conditionally upon proof of constitutional authority, standing, and lawful money accounting
**STEP 2 (Day 11)**: Second Notice/Opportunity to Cure - Provide supporting documentation, allow time to cure defects  
**STEP 3 (Day 16)**: Notice of Default - Declare default and dishonor for failure to provide lawful rebuttal
**STEP 4 (Day 19)**: Certificate of Non-Response - Administrative default established, silence = agreement
**STEP 5**: Remedy & Enforcement - Use administrative record for discharge and closure in honor

## Step 4: Administrative Default Importance
- **Shifts burden of proof** back to the claimant
- **Creates enforceable record** of opposing party's dishonor
- **Transforms controversy into agreement** through proper process
- **Maxim Applied**: "Equity aids the vigilant, not those who slumber on their rights"

## Step 5: Remedy in Honor
The Administrative Process achieves peaceful settlement without conflict. Conditional Acceptance under Article I Section 10 ensures all matters are resolved in honor with constitutional foundation.

---

**CRITICAL**: All fees and obligations must be settled ONLY in lawful money (constitutional gold and silver coin):
- Constitutional violations: $25,000 (in gold/silver coin only)
- Due process violations: $15,000 (in lawful money only)  
- Administrative default: $10,000 (in constitutional currency only)
- False claims: $25,000 (in gold/silver coin only)

**Article I Section 10**: "No State shall make any Thing but gold and silver Coin a Tender in Payment of Debts"
**Federal Reserve Notes are NOT constitutional money - only gold and silver coin accepted**

**This comprehensive three-section analysis provides complete Common Law Barrister guidance for your ${category} matter with constitutional protections and equitable remedies.**
`;

    return {
      category: category,
      fullResponse: fallbackContent,
      timestamp: new Date().toISOString(),
      conversationId: conversationId,
      uploadedDocuments: uploadedDocuments,
      summary: "Comprehensive three-section Common Law Barrister analysis with Legal Matter Overview, Equitable Rights Enforcement, and Administrative Process execution for your " + category + " matter.",
      plan: "5-step Administrative Process: (1)Conditional Acceptance → (2)Opportunity to Cure → (3)Default Notice → (4)Certificate of Non-Response → (5)Remedy & Enforcement. All via constitutional process with lawful money requirements.",
      guidance: "Complete framework incorporating English Common Law, Magna Carta, Equity Jurisprudence, and Article I Section 10 constitutional protections with three-section structure as specified.",
      warnings: "Maintain honor, clean hands, and constitutional compliance. Document everything, use proper certified mail procedures, and be prepared to follow through with complete administrative process in lawful money only."
    };
  };

  const handleGenerateDocuments = async () => {
    if (!remedy) {
      toast({
        title: "No Remedy Available",
        description: "Please generate a legal remedy first before creating documents.",
        variant: "destructive"
      });
      return;
    }

    setIsGeneratingDocuments(true);

    try {
      // Prepare case data for document generation with user-provided opposing party info
      const caseData = {
        full_name: user?.full_name || clientInfo.fullName || '[Your Full Name]',
        case_description: caseDescription,
        category: selectedCategory,
        court_name: '[Insert Name of Court]',
        case_number: '[Insert Case Number]',
        state: clientInfo.state || '[Your State]',
        address: clientInfo.streetAddress || '[Your Private Mailing Address]',
        city: clientInfo.city || '[City]',
        zip_code: clientInfo.zipCode || '[Zip Code]',
        client_email: clientInfo.email || user?.email || '[Your Email]',
        client_phone: clientInfo.phone || '[Your Phone]',
        opposing_party: opposingPartyInfo.fullName || '[Name of Opposing Party or Counsel]',
        opposing_address: opposingPartyInfo.fullAddress || '[Their Address]',
        country: clientInfo.country || 'United States'  // CRITICAL: Pass country for UK/US routing
      };

      const response = await axios.post(
        `${API_BASE}/conversations/${conversationId}/generate-documents`,
        caseData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success && response.data.job_id) {
        // Poll for completion (background job)
        const jobId = response.data.job_id;
        toast({
          title: "Generating Legal Documents",
          description: "Claude Sonnet 4.6 is drafting your documents. This may take 30-60 seconds...",
        });
        
        let attempts = 0;
        const maxAttempts = 60; // 60 x 3s = 180s max
        const pollInterval = 3000;
        
        const pollForDocs = async () => {
          while (attempts < maxAttempts) {
            attempts++;
            await new Promise(r => setTimeout(r, pollInterval));
            try {
              const pollRes = await axios.get(
                `${API_BASE}/conversations/${conversationId}/document-job/${jobId}`,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
              );
              if (pollRes.data.status === 'completed' && pollRes.data.documents && Object.keys(pollRes.data.documents).length > 0) {
                setGeneratedDocuments(pollRes.data.documents);
                await saveGeneratedDocuments(pollRes.data.documents, null);
                const docCount = Object.keys(pollRes.data.documents || {}).length;
                toast({
                  title: "Documents Generated Successfully",
                  description: `${docCount} AI-drafted legal documents are ready for download.`,
                });
                return;
              } else if (pollRes.data.status === 'completed' && pollRes.data.documents && Object.keys(pollRes.data.documents).length === 0) {
                throw new Error('Documents generated but content is empty. Please try again.');
              } else if (pollRes.data.status === 'failed') {
                const _errMsg = pollRes.data.error || 'Generation failed'; throw new Error(_errMsg.toLowerCase().includes('budget') ? 'Your AI key balance has been exceeded. Please go to Profile → Universal Key → Add Balance to continue.' : _errMsg);
              }
            } catch (pollErr) {
              if (pollErr.response?.status !== 200) {
                console.error('Poll error:', pollErr);
              }
            }
          }
          throw new Error('Document generation timed out. Please try again.');
        };
        
        await pollForDocs();
      } else if (response.data.success && response.data.documents) {
        // Direct response (fallback)
        setGeneratedDocuments(response.data.documents);
        await saveGeneratedDocuments(response.data.documents, null);
        const docCount = Object.keys(response.data.documents || {}).length;
        toast({
          title: "Documents Generated Successfully",
          description: `${docCount} legal documents are now ready for download.`,
        });
      } else {
        throw new Error("Failed to generate documents");
      }
    } catch (error) {
      console.error('Failed to generate documents:', error);
      toast({
        title: "Document Generation Failed",
        description: "Unable to generate legal documents. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingDocuments(false);
    }
  };

  // Auto-generation function for Tab 2 documents (called automatically after remedy generation)
  // Save generated documents back to the conversation and legal matter
  const saveGeneratedDocuments = async (docs, tab3Docs) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch existing conversation to merge metadata (don't overwrite)
      const convRes = await axios.get(`${API_BASE}/conversations/${conversationId}`, { headers });
      const existingMeta = convRes.data?.metadata || {};
      const mergedMeta = { ...existingMeta };
      if (docs) mergedMeta.generated_documents = docs;
      if (tab3Docs) mergedMeta.generated_tab3_documents = tab3Docs;
      
      await axios.put(`${API_BASE}/conversations/${conversationId}`, { metadata: mergedMeta }, { headers });
      
      // Also update legal matter if it exists
      const lmRes = await axios.get(`${API_BASE}/legal-matters?conversation_id=${conversationId}`, { headers });
      if (lmRes.data && lmRes.data.length > 0) {
        const matterId = lmRes.data[0].id;
        const lmUpdate = {};
        if (docs) lmUpdate.generated_documents = docs;
        if (tab3Docs) lmUpdate.generated_tab3_documents = tab3Docs;
        await axios.put(`${API_BASE}/legal-matters/${matterId}`, lmUpdate, { headers });
      }
      console.log('Generated documents saved to DB successfully');
    } catch (error) {
      console.error('Failed to save generated documents:', error);
    }
  };

  const autoGenerateTab2Documents = async () => {
    try {
      // Prepare case data for document generation
      const caseData = {
        full_name: clientInfo.fullName || user?.full_name || '[Your Full Name]',
        case_description: caseDescription,
        category: selectedCategory,
        court_name: '[Insert Name of Court]',
        case_number: '[Insert Case Number]',
        state: clientInfo.state || '[Your State]',
        address: clientInfo.streetAddress || '[Your Private Mailing Address]',
        city: clientInfo.city || '[City]',
        zip_code: clientInfo.zipCode || '[Zip Code]',
        client_email: clientInfo.email || '[Your Email]',
        client_phone: clientInfo.phone || '[Your Phone]',
        opposing_party: opposingPartyInfo.fullName || '[Name of Opposing Party or Counsel]',
        opposing_address: opposingPartyInfo.fullAddress || '[Their Address]',
        country: clientInfo.country || 'United States'  // CRITICAL: Pass country for UK/US routing
      };

      const response = await axios.post(
        `${API_BASE}/conversations/${conversationId}/generate-documents`,
        caseData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success && response.data.job_id) {
        // Poll for completion (background job)
        const jobId = response.data.job_id;
        let attempts = 0;
        const pollForDocs = async () => {
          while (attempts < 60) {
            attempts++;
            await new Promise(r => setTimeout(r, 3000));
            try {
              const pollRes = await axios.get(
                `${API_BASE}/conversations/${conversationId}/document-job/${jobId}`,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
              );
              if (pollRes.data.status === 'completed' && pollRes.data.documents && Object.keys(pollRes.data.documents).length > 0) {
                setGeneratedDocuments(pollRes.data.documents);
                await saveGeneratedDocuments(pollRes.data.documents, null);
                console.log("Tab 2 documents auto-generated successfully via Claude Sonnet 4.6");
                return;
              } else if (pollRes.data.status === 'failed') {
                throw new Error(pollRes.data.error);
              }
            } catch (e) {
              if (e.message) throw e;
            }
          }
        };
        await pollForDocs();
      } else if (response.data.success && response.data.documents) {
        setGeneratedDocuments(response.data.documents);
        console.log("Tab 2 documents auto-generated successfully");
        await saveGeneratedDocuments(response.data.documents, null);
      }
    } catch (error) {
      console.error('Failed to auto-generate Tab 2 documents:', error);
      // Don't show error toast for auto-generation failures
    }
  };

  // Auto-generation function for Tab 3 documents (called automatically after remedy generation)
  const autoGenerateTab3Documents = async () => {
    try {
      // Prepare case data for Tab 3 document generation
      const caseData = {
        full_name: clientInfo.fullName || user?.full_name || '[Your Full Name]',
        case_description: caseDescription,
        category: selectedCategory,
        court_name: '[Insert Name of Court]',
        case_number: '[Insert Case Number]',
        state: clientInfo.state || '[Your State]',
        address: clientInfo.streetAddress || '[Your Private Mailing Address]',
        city: clientInfo.city || '[City]',
        zip_code: clientInfo.zipCode || '[Zip Code]',
        client_email: clientInfo.email || '[Your Email]',
        client_phone: clientInfo.phone || '[Your Phone]',
        opposing_party: opposingPartyInfo.fullName || '[Name of Opposing Party or Counsel]',
        opposing_address: opposingPartyInfo.fullAddress || '[Their Address]',
        country: clientInfo.country || 'United States'  // CRITICAL: Pass country for Canadian/UK/US routing
      };

      const response = await axios.post(
        `${API_BASE}/conversations/${conversationId}/generate-tab3-documents`,
        caseData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setGeneratedTab3Documents(response.data.documents);
        console.log("Tab 3 documents auto-generated successfully");
        // Persist to DB so documents survive page reload
        await saveGeneratedDocuments(null, response.data.documents);
      }
    } catch (error) {
      console.error('Failed to auto-generate Tab 3 documents:', error);
      // Don't show error toast for auto-generation failures
    }
  };

  // Auto-generation function for Debt Discharge documents
  const autoGenerateDebtDischargeDocuments = async () => {
    try {
      const caseData = {
        full_name: clientInfo.fullName || user?.full_name || '[Your Full Name]',
        case_description: caseDescription,
        category: selectedCategory,
        court_name: '[Insert Name of Court]',
        case_number: '[Insert Case Number]',
        state: clientInfo.state || '[Your State]',
        address: clientInfo.streetAddress || '[Your Private Mailing Address]',
        city: clientInfo.city || '[City]',
        zip_code: clientInfo.zipCode || '[Zip Code]',
        client_email: clientInfo.email || '[Your Email]',
        client_phone: clientInfo.phone || '[Your Phone]',
        opposing_party: opposingPartyInfo.fullName || '[Name of Opposing Party or Counsel]',
        opposing_address: opposingPartyInfo.fullAddress || '[Their Address]',
        country: clientInfo.country || 'United States'
      };

      const response = await axios.post(
        `${API_BASE}/conversations/${conversationId}/generate-documents`,
        caseData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setGeneratedDebtDischargeDocuments(response.data.documents);
        console.log("Debt discharge documents auto-generated successfully");
      }
    } catch (error) {
      console.error('Failed to auto-generate debt discharge documents:', error);
      // Don't show error toast for auto-generation failures
    }
  };

  // Generate Debt Discharge Documents Function
  const handleGenerateDebtDischargeDocuments = async () => {
    if (!remedy) {
      toast({
        title: "No Remedy Available",
        description: "Please generate a legal remedy first before creating documents.",
        variant: "destructive"
      });
      return;
    }

    setIsGeneratingDebtDischargeDocuments(true);

    try {
      // First, generate the existing documents (Affidavit of Status, Testimony Under God, Notice of Rescission)
      const caseData = {
        full_name: clientInfo.fullName || user?.full_name || '[Your Full Name]',
        case_description: caseDescription,
        category: selectedCategory,
        court_name: '[Insert Name of Court]',
        case_number: '[Insert Case Number]',
        state: clientInfo.state || '[Your State]',
        address: clientInfo.streetAddress || '[Your Private Mailing Address]',
        city: clientInfo.city || '[City]',
        zip_code: clientInfo.zipCode || '[Zip Code]',
        client_email: clientInfo.email || '[Your Email]',
        client_phone: clientInfo.phone || '[Your Phone]',
        opposing_party: opposingPartyInfo.fullName || '[Name of Opposing Party or Counsel]',
        opposing_address: opposingPartyInfo.fullAddress || '[Their Address]',
        country: clientInfo.country || 'United States'
      };

      const response = await axios.post(
        `${API_BASE}/conversations/${conversationId}/generate-documents`,
        caseData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success && response.data.job_id) {
        // Poll for completion (background job)
        const jobId = response.data.job_id;
        toast({
          title: "Generating Documents",
          description: "AI is drafting your debt discharge documents. This may take 30-60 seconds...",
        });
        
        let attempts = 0;
        const maxAttempts = 60;
        const pollInterval = 3000;
        
        const pollForDocs = async () => {
          while (attempts < maxAttempts) {
            attempts++;
            await new Promise(r => setTimeout(r, pollInterval));
            try {
              const pollRes = await axios.get(
                `${API_BASE}/conversations/${conversationId}/document-job/${jobId}`,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
              );
              if (pollRes.data.status === 'completed' && pollRes.data.documents && Object.keys(pollRes.data.documents).length > 0) {
                setGeneratedDebtDischargeDocuments(pollRes.data.documents);
                await saveGeneratedDocuments(pollRes.data.documents, null);
                toast({
                  title: "Documents Generated Successfully",
                  description: "Debt discharge documents are now ready for download.",
                });
                return;
              } else if (pollRes.data.status === 'failed') {
                const _errMsg = pollRes.data.error || 'Generation failed';
                throw new Error(_errMsg.toLowerCase().includes('budget') ? 'Your AI key balance has been exceeded. Please go to Profile → Universal Key → Add Balance to continue.' : _errMsg);
              }
            } catch (pollErr) {
              if (pollErr.response?.status !== 200) {
                console.error('Poll error:', pollErr);
              }
            }
          }
          throw new Error('Document generation timed out. Please try again.');
        };
        
        await pollForDocs();
      } else if (response.data.success && response.data.documents) {
        // Direct response (fallback)
        setGeneratedDebtDischargeDocuments(response.data.documents);
        
        toast({
          title: "Documents Generated Successfully",
          description: "Debt discharge documents are now ready for download.",
        });
      } else {
        throw new Error("Failed to generate documents");
      }
    } catch (error) {
      console.error('Failed to generate debt discharge documents:', error);
      toast({
        title: "Document Generation Failed",
        description: "Unable to generate debt discharge documents. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingDebtDischargeDocuments(false);
    }
  };

  // Download handler for Debt Discharge Conditional Acceptance (generated on-the-fly)
  const handleDownloadDebtDischargeConditionalAcceptance = async () => {
    try {
      const templateContent = getDebtDischargeConditionalAcceptanceTemplate();
      const filename = selectedCategory === 'tax-collection' 
        ? 'Notice_of_Conditional_Acceptance_Tax.docx'
        : 'Notice_of_Conditional_Acceptance_Contract.docx';
      
      // Send to backend to convert to DOCX
      const response = await axios.post(
        `${API_BASE}/documents/download-docx`,
        { 
          content: templateContent, 
          filename: filename.replace('.docx', ''),
          client_info: clientInfo
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      // Download the DOCX
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
      
      toast({
        title: "Document Downloaded",
        description: `${filename} has been downloaded.`,
      });
    } catch (error) {
      console.error('Failed to download conditional acceptance:', error);
      toast({
        title: "Download Failed",
        description: "Unable to download document. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Download handler for Debt Discharge Notice of Discharge Final (Second Mailing)
  const handleDownloadDebtDischargeNoticeDischargeFinal = async () => {
    try {
      const templateContent = getDebtDischargeNoticeDischargeFinalTemplate();
      const filename = 'Notice_of_Discharge_Final.docx';
      
      // Send to backend to convert to DOCX
      const response = await axios.post(
        `${API_BASE}/documents/download-docx`,
        { 
          content: templateContent, 
          filename: filename.replace('.docx', ''),
          client_info: clientInfo
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      // Download the DOCX
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
      
      toast({
        title: "Document Downloaded",
        description: `${filename} has been downloaded.`,
      });
    } catch (error) {
      console.error('Failed to download notice of discharge final:', error);
      toast({
        title: "Download Failed",
        description: "Unable to download document. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Download handler for Debt Discharge documents (reusing existing documents)
  const handleDownloadDebtDischargeDocument = async (documentType, filename) => {
    if (!generatedDebtDischargeDocuments || !generatedDebtDischargeDocuments[documentType]) {
      toast({
        title: "Document Not Available",
        description: "Please generate documents first.",
        variant: "destructive"
      });
      return;
    }

    try {
      const documentData = generatedDebtDischargeDocuments[documentType];
      
      if (documentData && typeof documentData === 'object' && documentData.content) {
        const binaryData = atob(documentData.content);
        const uint8Array = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          uint8Array[i] = binaryData.charCodeAt(i);
        }
        
        const blob = new Blob([uint8Array], { 
          type: documentData.mime_type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = documentData.filename || filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
        
        toast({
          title: "Document Downloaded",
          description: `${documentData.filename || filename} has been downloaded.`,
        });
      } else {
        const content = typeof documentData === 'string' ? documentData : JSON.stringify(documentData);
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
        
        toast({
          title: "Document Downloaded",
          description: `${filename} has been downloaded.`,
        });
      }
    } catch (error) {
      console.error('Failed to download debt discharge document:', error);
      toast({
        title: "Download Failed",
        description: "Unable to download document. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Reliable download helper - 2-step: POST to prepare, then trigger GET download via hidden iframe
  const downloadDocxFile = (base64Content, filename) => {
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 2000);
    return true;
  };

  // Check if a generated document was flagged as not applicable
  const isDocumentRefused = (docKey) => {
    const doc = generatedDocuments?.[docKey];
    return doc && typeof doc === 'object' && doc.refused === true;
  };
  const getRefusalReason = (docKey) => {
    const doc = generatedDocuments?.[docKey];
    return (doc && typeof doc === 'object' && doc.reason) || 'The AI determined this document is not applicable to your case.';
  };

  const handleDownloadDocument = async (documentType, filename) => {
    // Try the exact key first, then fallback to standard keys
    const fallbackKeys = {
      'testimony_under_god_uk': 'testimony_under_god_rescission',
      'testimony_under_god_canada': 'testimony_under_god_rescission',
      'testimony_under_god_australia': 'testimony_under_god_rescission',
      'affidavit_of_status_uk': 'affidavit_of_status',
      'affidavit_of_status_canada': 'affidavit_of_status',
      'affidavit_of_status_australia': 'affidavit_of_status',
      'conditional_acceptance_uk': 'notice_of_conditional_acceptance',
      'conditional_acceptance_canada': 'notice_of_conditional_acceptance',
      'conditional_acceptance_australia': 'notice_of_conditional_acceptance',
    };
    
    console.log('[DOWNLOAD DEBUG] documentType:', documentType);
    console.log('[DOWNLOAD DEBUG] generatedDocuments type:', typeof generatedDocuments);
    console.log('[DOWNLOAD DEBUG] generatedDocuments keys:', generatedDocuments ? Object.keys(generatedDocuments) : 'null');
    
    let docData = generatedDocuments?.[documentType];
    if (!docData && fallbackKeys[documentType]) {
      docData = generatedDocuments?.[fallbackKeys[documentType]];
      console.log('[DOWNLOAD DEBUG] Used fallback key:', fallbackKeys[documentType], 'found:', !!docData);
    }
    
    // If still not found, try to find ANY matching key
    if (!docData && generatedDocuments) {
      const availableKeys = Object.keys(generatedDocuments);
      console.log('[DOWNLOAD DEBUG] Available keys:', availableKeys);
      // Try partial match
      const partialMatch = availableKeys.find(k => 
        documentType.includes(k) || k.includes(documentType.split('_').slice(0, 3).join('_'))
      );
      if (partialMatch) {
        docData = generatedDocuments[partialMatch];
        console.log('[DOWNLOAD DEBUG] Partial match found:', partialMatch);
      }
    }
    
    if (!generatedDocuments || !docData) {
      // Last resort: try to reload from conversation metadata
      console.log('[DOWNLOAD DEBUG] Document not found, attempting reload from server...');
      try {
        const token = localStorage.getItem('token');
        const convRes = await axios.get(`${API_BASE}/conversations/${conversationId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const savedDocs = convRes.data?.metadata?.generated_documents;
        if (savedDocs && Object.keys(savedDocs).length > 0) {
          console.log('[DOWNLOAD DEBUG] Reloaded docs from server:', Object.keys(savedDocs));
          setGeneratedDocuments(savedDocs);
          docData = savedDocs[documentType] || savedDocs[fallbackKeys[documentType]];
          if (!docData) {
            // Try first available key match
            const firstMatch = Object.keys(savedDocs).find(k => 
              documentType.includes(k) || k.includes(documentType.split('_').slice(0, 3).join('_'))
            );
            if (firstMatch) docData = savedDocs[firstMatch];
          }
        }
      } catch (reloadErr) {
        console.error('[DOWNLOAD DEBUG] Failed to reload:', reloadErr);
      }
      
      if (!docData) {
        toast({
          title: "Document Not Available",
          description: "Please generate documents first.",
          variant: "destructive"
        });
        return;
      }
    }

    try {
      // Check if document was flagged as not applicable for this case
      if (docData && typeof docData === 'object' && docData.refused) {
        toast({
          title: "Document Not Applicable",
          description: docData.reason || "The AI determined this document is not needed for your particular case.",
          variant: "destructive",
          duration: 12000,
        });
        return;
      }
      if (docData && typeof docData === 'object' && docData.content) {
        const downloadFilename = docData.filename || filename.replace('.txt', '.docx');
        const success = await downloadDocxFile(docData.content, downloadFilename);
        if (success) {
          toast({ title: "Document Downloaded", description: `${downloadFilename} has been downloaded.` });
        } else {
          toast({ title: "Download Failed", description: "Unable to download document.", variant: "destructive" });
        }
      } else {
        // Fallback for plain text
        const content = typeof docData === 'string' ? docData : JSON.stringify(docData);
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 2000);
        toast({ title: "Document Downloaded", description: `${filename} has been downloaded.` });
      }
    } catch (error) {
      console.error('Failed to download document:', error);
      toast({ title: "Download Failed", description: "Unable to download document. Please try again.", variant: "destructive" });
    }
  };

  const handleGenerateTab3Documents = async () => {
    if (!remedy) {
      toast({
        title: "No Remedy Available",
        description: "Please generate a legal remedy first before creating administrative documents.",
        variant: "destructive"
      });
      return;
    }

    setIsGeneratingTab3Documents(true);

    try {
      // Prepare case data for Tab 3 document generation
      const caseData = {
        full_name: clientInfo.fullName || user?.full_name || '[Your Full Name]',
        case_description: caseDescription,
        category: selectedCategory,
        court_name: '[Insert Name of Court]',
        case_number: '[Insert Case Number]',
        state: clientInfo.state || '[Your State]',
        address: clientInfo.streetAddress || '[Your Private Mailing Address]',
        city: clientInfo.city || '[City]',
        zip_code: clientInfo.zipCode || '[Zip Code]',
        client_email: clientInfo.email || '[Your Email]',
        client_phone: clientInfo.phone || '[Your Phone]',
        opposing_party: opposingPartyInfo.fullName || '[Name of Opposing Party or Counsel]',
        opposing_address: opposingPartyInfo.fullAddress || '[Their Address]',
        country: clientInfo.country || 'United States',
        respondent_response: respondentResponse3?.extractedText || '',
      };

      const response = await axios.post(
        `${API_BASE}/conversations/${conversationId}/generate-tab3-documents`,
        caseData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setGeneratedTab3Documents(response.data.documents);
        toast({
          title: "Administrative Documents Generated Successfully",
          description: "Three administrative process documents are now ready for download.",
        });
      } else {
        throw new Error("Failed to generate administrative documents");
      }
    } catch (error) {
      console.error('Failed to generate Tab 3 documents:', error);
      toast({
        title: "Administrative Document Generation Failed",
        description: "Unable to generate administrative documents. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingTab3Documents(false);
    }
  };

  // Handle respondent response file upload for Second/Third Mailing
  const handleResponseUpload = async (event, mailingStep) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain', 'image/jpeg', 'image/png', 'image/jpg'
    ];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Invalid File Type", description: "Supported: PDF, DOCX, DOC, TXT, JPG, PNG", variant: "destructive" });
      event.target.value = '';
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Maximum file size is 50MB", variant: "destructive" });
      event.target.value = '';
      return;
    }

    setIsUploadingResponse(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tag', 'respondent_response');
      formData.append('description', `Respondent response uploaded for ${mailingStep}`);
      const res = await axios.post(
        `${API_BASE}/api/attorney/case-documents/${conversationId}/upload`,
        formData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const responseData = {
        id: res.data.doc_id || `resp-${Date.now()}`,
        name: file.name,
        size: file.size,
        type: file.type,
        extractedText: res.data.extracted_text || '',
        uploadedAt: new Date().toISOString(),
      };
      if (mailingStep === 'second_mailing') {
        setRespondentResponse2(responseData);
      } else {
        setRespondentResponse3(responseData);
      }
      toast({
        title: "Response Uploaded",
        description: responseData.extractedText
          ? `Extracted ${responseData.extractedText.length} characters from ${file.name}. The AI will address this response in the next document.`
          : `${file.name} uploaded. No text could be extracted.`,
      });
    } catch (err) {
      console.error('Response upload error:', err);
      toast({ title: "Upload Failed", description: err.response?.data?.detail || 'Could not upload response file.', variant: "destructive" });
    } finally {
      setIsUploadingResponse(false);
      event.target.value = '';
    }
  };

  const removeRespondentResponse = (mailingStep) => {
    if (mailingStep === 'second_mailing') {
      setRespondentResponse2(null);
    } else {
      setRespondentResponse3(null);
    }
    toast({ title: "Response Removed", description: "Respondent response has been removed." });
  };

  const handleDownloadTab3Document = async (documentType, filename) => {
    if (!generatedTab3Documents || !generatedTab3Documents[documentType]) {
      toast({
        title: "Document Not Available",
        description: "Please generate administrative documents first.",
        variant: "destructive"
      });
      return;
    }

    try {
      const documentData = generatedTab3Documents[documentType];
      
      if (documentData && typeof documentData === 'object' && documentData.content) {
        const downloadFilename = documentData.filename || filename.replace('.txt', '.docx');
        const success = await downloadDocxFile(documentData.content, downloadFilename);
        if (success) {
          toast({ title: "Document Downloaded", description: `${downloadFilename} has been downloaded.` });
        } else {
          toast({ title: "Download Failed", description: "Unable to download document.", variant: "destructive" });
        }
      } else {
        const content = typeof documentData === 'string' ? documentData : JSON.stringify(documentData);
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 2000);
        toast({ title: "Document Downloaded", description: `${filename} has been downloaded.` });
      }
    } catch (error) {
      console.error('Failed to download Tab 3 document:', error);
      toast({ title: "Download Failed", description: "Unable to download document. Please try again.", variant: "destructive" });
    }
  };

  const handleDownloadCourtRoomScript = async () => {
    console.log("handleDownloadCourtRoomScript called for:", caseType); // Debug log
    
    // For Petitioner workflow - use OpenAI with your Court Room Script template
    if (caseType === 'petitioner') {
      if (!selectedCategory || !caseDescription.trim()) {
        toast({
          title: "Missing Information",
          description: "Please select a petition category and provide case description before generating court script.",
          variant: "destructive"
        });
        return;
      }

      if (!clientInfo.fullName) {
        toast({
          title: "Missing Client Information",
          description: "Please provide client name before generating court script.",
          variant: "destructive"
        });
        return;
      }

      console.log("Starting OpenAI court script generation for petitioner..."); // Debug log

      try {
        // Prepare court script data
        const scriptData = {
          client_info: clientInfo,
          opposing_party_info: opposingPartyInfo,
          category: selectedCategory,
          case_description: caseDescription,
          case_type: caseType
        };

        const response = await axios.post(`${API_BASE}/conversations/${conversationId}/generate-court-script`, scriptData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.content) {
          // Decode base64 content to binary for DOCX
          const binaryString = atob(response.data.content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Create DOCX blob
          const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = response.data.filename || 'Court_Room_Script_Petitioner.docx';
          document.body.appendChild(a);
          a.click();
          setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
          
          toast({
            title: "Court Room Script Downloaded",
            description: "Your personalized court room script has been generated and downloaded as a Word document.",
          });
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Failed to download petitioner court room script:', error);
        toast({
          title: "Download Failed",
          description: "Unable to generate court room script. Please try again.",
          variant: "destructive"
        });
      }
    } else {
      // For Defendant workflow - use country-specific courtroom script templates
      console.log("Generating country-specific court script for defendant..."); // Debug log
      
      if (!clientInfo.fullName) {
        toast({
          title: "Missing Client Information",
          description: "Please provide client name before generating court script.",
          variant: "destructive"
        });
        return;
      }
      
      try {
        // Prepare case data for courtroom script
        const scriptData = {
          full_name: clientInfo.fullName,
          opposing_party: opposingPartyInfo.name || '[Opposing Party]',
          country: clientInfo.country || 'United States'
        };

        const response = await axios.post(`${API_BASE}/conversations/${conversationId}/generate-defendant-courtroom-script`, scriptData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.content) {
          // Decode base64 content to binary for DOCX
          const binaryString = atob(response.data.content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Create DOCX blob
          const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = response.data.filename || 'Courtroom_Script_Defendant.docx';
          document.body.appendChild(a);
          a.click();
          setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
          
          toast({
            title: "Court Room Script Downloaded",
            description: "Your personalized court room script has been generated and downloaded.",
          });
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Failed to download defendant court room script:', error);
        toast({
          title: "Download Failed",
          description: "Unable to generate court room script. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  const handleDownloadFOIARequest = async () => {
    try {
      // Generate FOIA request content with user's information
      const foiaContent = generateFOIARequest();
      
      // Download as DOCX using the backend endpoint
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;
      
      // Prepare request body - include client_info for UK FOIA, Canadian ATIA, and Australian FOI population
      const requestBody = { 
        content: foiaContent,
        filename: clientInfo.country === "The United Kingdom" 
          ? 'UK_Freedom_of_Information_Request' 
          : clientInfo.country === "Canada"
          ? 'Canada_Access_to_Information_Request'
          : clientInfo.country === "Australia"
          ? 'Australia_Freedom_of_Information_Request'
          : 'FOIA_Public_Records_Request',
        client_info: {
          ...clientInfo,
          opposingParty: opposingPartyInfo.fullName || '[Name of Government Institution]'
        }  // Pass client info and opposing party for template population
      };
      
      const response = await fetch(`${backendUrl}/api/documents/download-docx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = clientInfo.country === "The United Kingdom" 
          ? 'UK_Freedom_of_Information_Request.docx'
          : clientInfo.country === "Canada"
          ? 'Canada_Access_to_Information_Request.docx'
          : clientInfo.country === "Australia"
          ? 'Australia_Freedom_of_Information_Request.docx'
          : 'FOIA_Public_Records_Request.docx';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { window.URL.revokeObjectURL(url); document.body.removeChild(a); }, 1000);
        
        toast({
          title: clientInfo.country === "The United Kingdom" 
            ? "UK FOIA Request Downloaded"
            : clientInfo.country === "Canada"
            ? "Canadian ATIA Request Downloaded"
            : clientInfo.country === "Australia"
            ? "Australian FOI Request Downloaded"
            : "FOIA Request Downloaded",
          description: clientInfo.country === "The United Kingdom"
            ? "Your personalized UK Freedom of Information request has been downloaded as a Word document."
            : clientInfo.country === "Canada"
            ? "Your personalized Canadian Access to Information Act request has been downloaded as a Word document."
            : clientInfo.country === "Australia"
            ? "Your personalized Australian Freedom of Information Act 1982 request has been downloaded as a Word document."
            : "Your personalized FOIA request has been downloaded as a Word document.",
        });
      } else {
        throw new Error('DOCX generation failed');
      }
    } catch (error) {
      console.error('Failed to download FOIA request:', error);
      toast({
        title: "Download Failed",
        description: "Unable to download FOIA request. Please try again.",
        variant: "destructive"
      });
    }
  };

  const generateFOIARequest = () => {
    // Check if UK user - if so, return placeholder (backend will generate UK FOIA)
    if (clientInfo.country === "The United Kingdom") {
      return "UK_FOIA_REQUEST"; // Placeholder - backend will generate actual UK FOIA
    }
    
    // Check if Canadian user - if so, return placeholder (backend will generate Canadian ATIA)
    if (clientInfo.country === "Canada") {
      return "CANADA_ATIA_REQUEST"; // Placeholder - backend will generate actual Canadian Access to Information Act request
    }
    
    // Check if Australian user - if so, return placeholder (backend will generate Australian FOI)
    if (clientInfo.country === "Australia") {
      return "AUSTRALIA_FOI_REQUEST"; // Placeholder - backend will generate actual Australian Freedom of Information Act 1982 request
    }
    
    // US FOIA generation (existing code)
    const clientName = clientInfo.fullName || '[Your Name]';
    const clientAddress = clientInfo.streetAddress || '[Your Address]';
    const clientCity = clientInfo.city || '';
    const clientState = clientInfo.state || '';
    const clientZip = clientInfo.zipCode || '';
    const fullAddress = `${clientAddress}${clientCity ? ', ' + clientCity : ''}${clientState ? ', ' + clientState : ''}${clientZip ? ' ' + clientZip : ''}`;
    const opposingParty = opposingPartyInfo.fullName || '[Opposing Party]';
    
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'long', 
      year: 'numeric' 
    });
    
    // Use the exact format from the new FOIA PDF document
    return `Public Records Request (FOIA / State Open Records Act)

${clientName}
${fullAddress}

Date: ${formattedDate}

To:
Clerk of the Court & Opposing Attorneys
[Clerk Address]
[Opposing Party Address]

Subject: Request for Certified Copies of Oaths of Office, Qualifications, and Bonds

NOTICE

This is a request made pursuant to:
• U.S. Freedom of Information Act (5 U.S.C. § 552)

REQUEST

On behalf of myself, ${clientName}, I respectfully request official certified copies of the following public records:

1. The Oath of Office for:
   a. Clerk of Court (current holder)
   b. District Court Judge (current holder)
   c. District Attorney (current holder)
   d. Assistant District Attorney(s) involved in my matter
   e. Opposing Party's Attorney or Representatives

2. The Qualifications filed by the above officials.

3. The Official Bonds (or alternatively, any financial disclosures required by law if no bond is filed).

4. FOREIGN AGENTS REGISTRATION ACT (FARA) RECORDS

I respectfully request access to and copies of all available records, registration statements, supplemental filings, exhibits, correspondence, and related documents submitted under the Foreign Agents Registration Act (FARA) concerning:

Registrant Name: [Insert Name of Prosecuting Attorney(s)/Clerk(s)/etc, Individual(s)/Organization]

Foreign Principal (if known): [Insert Name of Government, Party, or Entity]

Date Range: [Specify years, e.g., 2015–2025]

If these records are available through the DOJ's FARA Public Database, please include any non-digitized filings or additional supporting exhibits not accessible online.

LEGAL BASIS

• The U.S. Constitution, Article VI, Clause 3 requires an oath of office for all officers.
• The State Constitution and statutes similarly mandate sworn oaths and bonds for public officials.
• Case law (e.g., Marbury v. Madison, 5 U.S. 137 (1803)) underscores that lawful authority must be demonstrable.

DELIVERY & DEADLINE

Please provide these records within 10 business days of receipt of this request, consistent with the Texas Public Information Act. If your office does not maintain these records, kindly forward this request to the appropriate custodian.

If the cost exceeds $20, notify me in advance.

PURPOSE

This request is made to verify that all acting officials have met the constitutional and statutory requirements of their offices. This ensures the People's right to due process of law and government accountability.

Respectfully,

${clientName}
${fullAddress}`;
  };

  const handleDownloadPetition = async () => {
    console.log("handleDownloadPetition called"); // Debug log
    
    if (!selectedCategory || !caseDescription.trim()) {
      toast({
        title: "Missing Information",
        description: "Please select a petition category and provide case description before downloading petition.",
        variant: "destructive"
      });
      return;
    }

    if (!clientInfo.fullName || !clientInfo.email) {
      toast({
        title: "Missing Client Information",
        description: "Please provide client name and email before downloading petition.",
        variant: "destructive"
      });
      return;
    }

    console.log("Starting petition generation..."); // Debug log

    try {
      // Prepare petition data
      const petitionData = {
        client_info: clientInfo,
        opposing_party_info: opposingPartyInfo,
        category: selectedCategory,
        case_description: caseDescription,
        case_type: caseType
      };

      console.log("Petition data:", petitionData); // Debug log

      const response = await axios.post(`${API_BASE}/conversations/${conversationId}/generate-petition`, petitionData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      console.log("API response:", response.data); // Debug log

      if (response.data && response.data.content) {
        console.log("Processing DOCX content..."); // Debug log
        
        // Decode base64 content to binary
        const binaryString = atob(response.data.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Create DOCX blob
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename || 'Custom_Petition_in_Equity.docx';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
        
        console.log("Download completed successfully"); // Debug log
        
        toast({
          title: "Petition Downloaded",
          description: "Your custom petition has been generated and downloaded as a Word document.",
        });
      } else {
        console.error("Invalid response format:", response.data); // Debug log
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to download petition:', error); // Debug log
      toast({
        title: "Download Failed",
        description: "Unable to generate petition. Please try again.",
        variant: "destructive"
      });
    }
  };

  const generatePetitionContent = () => {
    const clientName = clientInfo.fullName || user?.full_name || '[Your Name]';
    const clientAddress = clientInfo.streetAddress || '[Your Address]';
    const clientCity = clientInfo.city || '';
    const clientState = clientInfo.state || '';
    const clientZip = clientInfo.zipCode || '';
    const fullAddress = `${clientAddress}${clientCity ? ', ' + clientCity : ''}${clientState ? ', ' + clientState : ''}${clientZip ? ' ' + clientZip : ''}`;
    const opposingParty = opposingPartyInfo.fullName || '[Opposing Party]';
    const categoryLabel = petitionerCategories.find(cat => cat.value === selectedCategory)?.label || selectedCategory.replace('-', ' ').toUpperCase();
    
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'long', 
      year: 'numeric' 
    });
    
    return `PETITION FOR ${categoryLabel.toUpperCase()}

IN THE MATTER OF: ${clientName}

TO THE HONORABLE COURT:

COMES NOW ${clientName}, appearing pro se as a living man/woman, not a corporate fiction, and respectfully petitions this Honorable Court for relief in the matter of ${categoryLabel} as follows:

JURISDICTION AND STANDING

1. This Court has jurisdiction over this matter pursuant to the Constitution and laws of this State, and the petitioner has standing as a living man/woman with inherent, unalienable rights protected by the Magna Carta (1215), English Bill of Rights (1689), and the United States Constitution.

2. Petitioner appears under the authority of English Common Law, Equity Jurisprudence, and Constitutional protections, asserting rights that exist independent of statutory grants or permissions.

STATEMENT OF FACTS

3. The matter before this Court concerns: ${caseDescription}

4. Petitioner seeks relief under the category of: ${categoryLabel}

5. The opposing party in this matter is: ${opposingParty}

CAUSE OF ACTION IN EQUITY

6. This petition is brought under the Maxims of Equity, specifically:
   - "Equity will not suffer a wrong without a remedy"
   - "Equity follows the law" 
   - "He who seeks equity must do equity"

7. Petitioner has clean hands and comes before this Court in honor, seeking equitable relief where legal remedies are inadequate.

CONDITIONAL ACCEPTANCE

8. Petitioner conditionally accepts any claims made by opposing parties, upon proof of:
   a. Constitutional authority and jurisdiction over petitioner's person
   b. Verified contract establishing such authority
   c. Evidence of actual harm to a living man or woman
   d. Full accounting in lawful money (constitutional gold and silver coin per Article I Section 10)

PRAYER FOR RELIEF

WHEREFORE, petitioner respectfully requests that this Honorable Court:

1. Grant the relief sought under the ${categoryLabel} category;
2. Recognize petitioner's constitutional and equitable rights;
3. Require opposing parties to provide proof of their claims and authority;
4. Award such other relief as this Court deems just and proper in equity.

HONOR AND CLEAN HANDS

Petitioner affirms that this petition is brought in honor, with clean hands, and in accordance with the highest principles of law and equity.

FORMAT AND STRUCTURE

This petition is respectfully submitted under the authority of:
- Magna Carta, Clause 39 (1215)
- English Bill of Rights (1689)
- U.S. Constitution, Article I Section 10
- Maxims of Equity and Common Law principles

PROOF OF SERVICE

I hereby certify that a true and correct copy of this petition has been served upon all interested parties by certified mail, return receipt requested.

Respectfully submitted,

_________________________
${clientName}
${fullAddress}
Appearing Pro Se

Date: ${formattedDate}

VERIFICATION

I, ${clientName}, being duly sworn, depose and say that I am the petitioner in the above-entitled matter; that I have read the foregoing petition and know the contents thereof; and that the same is true to my own knowledge, except as to those matters therein stated upon information and belief, and as to those matters, I believe it to be true.

_________________________
${clientName}

Subscribed and sworn to before me this _____ day of _________, 20__.

_________________________
Notary Public`;
  };

  const generateCourtRoomScript = () => {
    const clientName = clientInfo.fullName || '[Your Name]';
    const opposingParty = opposingPartyInfo.fullName || '[Opposing Party Name]';
    const matter = caseDescription || '[Case Description]';
    const category = selectedCategory || 'general';
    
    return `COURT ROOM SCRIPT - ${category.toUpperCase()} MATTER

==================================================
PERSONAL COURT ROOM SCRIPT
==================================================

Case Information:
- Your Name: ${clientName}
- Opposing Party: ${opposingParty}
- Matter Type: ${category.replace('-', ' ').toUpperCase()}
- Case Description: ${matter}

==================================================
OPENING STATEMENT
==================================================

"Your Honor, I am ${clientName}, appearing pro se in this matter. I am here today to present evidence and testimony regarding ${matter}. I believe the facts will show that [state your position clearly]."

==================================================
KEY OBJECTIONS TO REMEMBER
==================================================

1. "Objection, Your Honor - Hearsay"
   (When opposing party tries to introduce second-hand information)

2. "Objection, Your Honor - Relevance"
   (When information doesn't relate to the case)

3. "Objection, Your Honor - Leading the witness"
   (When opposing counsel asks leading questions)

4. "Objection, Your Honor - Asked and answered"
   (When same question is repeated)

==================================================
PRESENTATION OF EVIDENCE
==================================================

"Your Honor, I would like to present the following evidence:
1. [List your documents/evidence]
2. [Explain relevance of each piece]
3. [Request admission into evidence]"

"I request that this be marked as Exhibit A and admitted into evidence."

==================================================
QUESTIONING WITNESSES
==================================================

For your own witnesses:
- "Please state your name for the record"
- "How do you know about this matter?"
- "What did you observe/hear?"
- "Are you certain about what you witnessed?"

For opposing witnesses:
- Stay calm and professional
- Ask short, clear questions
- Don't argue with witnesses
- Focus on facts, not emotions

==================================================
CLOSING ARGUMENT
==================================================

"Your Honor, based on the evidence presented today, I respectfully request that you find in my favor because:
1. [Key point 1 with evidence]
2. [Key point 2 with evidence]
3. [Key point 3 with evidence]

The law is clear on this matter, and the facts support my position. Thank you for your time and consideration."

==================================================
IMPORTANT REMINDERS
==================================================

• Stay calm and respectful at all times
• Address the judge as "Your Honor"
• Stand when speaking unless told otherwise
• Never interrupt the judge or opposing counsel
• Speak clearly and at an appropriate volume
• Bring multiple copies of all documents
• Arrive early to familiarize yourself with the courtroom

==================================================
CONSTITUTIONAL RIGHTS REMINDERS
==================================================

• Right to due process under the 14th Amendment
• Right to present evidence and witnesses
• Right to cross-examine opposing witnesses
• Right to be heard in your defense
• Right to equal protection under the law

==================================================
EMERGENCY PHRASES
==================================================

If you're unsure: "Your Honor, may I have a moment to review my notes?"
If you need clarification: "Your Honor, could you please repeat the question?"
If you need time: "Your Honor, I respectfully request a brief recess to organize my thoughts."

==================================================

This script is personalized for your ${category} matter.
Review thoroughly before your court appearance.
Practice key phrases and prepare your evidence.

Good luck with your case!

==================================================`;
  };

  const parseAIResponse = (response) => {
    // Parse the AI response into structured sections
    const sections = {
      summary: '',
      plan: '',
      guidance: '',
      warnings: ''
    };

    // Split response by common section markers
    const lines = response.split('\n');
    let currentSection = '';

    lines.forEach(line => {
      const lowerLine = line.toLowerCase();
      
      if (lowerLine.includes('issue summary') || lowerLine.includes('analysis') || lowerLine.includes('assessment')) {
        currentSection = 'summary';
      } else if (lowerLine.includes('step-by-step') || lowerLine.includes('plan') || lowerLine.includes('roadmap')) {
        currentSection = 'plan';
      } else if (lowerLine.includes('educational') || lowerLine.includes('guidance') || lowerLine.includes('rights')) {
        currentSection = 'guidance';
      } else if (lowerLine.includes('warning') || lowerLine.includes('boundary') || lowerLine.includes('disclaimer')) {
        currentSection = 'warnings';
      } else if (line.trim() && currentSection) {
        sections[currentSection] += line + '\n';
      }
    });

    // If parsing fails, put everything in summary
    if (!sections.summary && !sections.plan && !sections.guidance) {
      sections.summary = response;
    }

    return {
      category: selectedCategory,
      fullResponse: response,
      ...sections
    };
  };

  const handleRefineRemedy = () => {
    // Allow user to refine the remedy
    toast({
      title: "Refine Remedy",
      description: "You can now modify your case description or ask follow-up questions in the chat.",
    });
  };

  const handleDownload = () => {
    if (!remedy) return;
    
    // Create a text file with the remedy
    const content = `Legal Remedy Analysis - ${remedy.category}\n\n${remedy.fullResponse}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `legal-remedy-${remedy.category}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Download Started",
      description: "Your legal remedy document is downloading.",
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Lawful Remedy Generator</h1>
        <p className="text-gray-600">Common Law Barrister Analysis & Document Creation</p>
      </div>

      {/* Case Type Selection - NEW */}
      {!caseType && (
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Scale className="w-5 h-5" />
              Select Your Legal Position
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-blue-700 mb-6 text-center">
              Are you seeking to defend against charges/claims (Defendant) or are you petitioning the court for relief (Petitioner)?
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div 
                onClick={async () => {
                  setCaseType('defendant');
                  localStorage.setItem('preferred_case_type', 'defendant');
                  
                  // Immediately save case type to conversation metadata
                  try {
                    await axios.put(`${API_BASE}/conversations/${conversationId}`, {
                      metadata: {
                        case_type: 'defendant'
                      }
                    }, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      }
                    });
                  } catch (error) {
                    console.error('Failed to save case type:', error);
                  }
                }}
                className="cursor-pointer p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all"
              >
                <div className="text-center">
                  <div className="text-4xl mb-3">🛡️</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">I am a Defendant</h3>
                  <p className="text-sm text-gray-600">
                    I am defending against charges, claims, or legal actions brought against me.
                    I need help with remedy generation and defensive strategies.
                  </p>
                </div>
              </div>
              <div 
                onClick={async () => {
                  setCaseType('petitioner');
                  localStorage.setItem('preferred_case_type', 'petitioner');
                  
                  // Immediately save case type to conversation metadata
                  try {
                    await axios.put(`${API_BASE}/conversations/${conversationId}`, {
                      metadata: {
                        case_type: 'petitioner'
                      }
                    }, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      }
                    });
                  } catch (error) {
                    console.error('Failed to save case type:', error);
                  }
                }}
                className="cursor-pointer p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all"
              >
                <div className="text-center">
                  <div className="text-4xl mb-3">📋</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">I am a Petitioner</h3>
                  <p className="text-sm text-gray-600">
                    I am petitioning the court for relief, enforcement of rights, 
                    or requesting the court to take specific action.
                  </p>
                </div>
              </div>
              <div 
                onClick={async () => {
                  setCaseType('debt_discharge');
                  localStorage.setItem('preferred_case_type', 'debt_discharge');
                  
                  // Save case type to conversation metadata
                  try {
                    await axios.put(`${API_BASE}/conversations/${conversationId}`, {
                      metadata: {
                        case_type: 'debt_discharge'
                      }
                    }, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      }
                    });
                  } catch (error) {
                    console.error('Failed to save case type:', error);
                  }
                }}
                className="cursor-pointer p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-green-400 hover:shadow-md transition-all"
              >
                <div className="text-center">
                  <div className="text-4xl mb-3">💳</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Debt Discharge</h3>
                  <p className="text-sm text-gray-600">
                    I need help discharging consumer debt, tax obligations, 
                    or other financial obligations through proper legal remedy.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Show workflow only after case type is selected */}
      {caseType && (
        <>
          {/* Case Type Header */}
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center gap-2">
              <Badge className={`${
                caseType === 'petitioner' ? 'bg-green-100 text-green-700' : 
                caseType === 'debt_discharge' ? 'bg-yellow-100 text-yellow-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {caseType === 'petitioner' ? '📋 Petitioner Workflow' : 
                 caseType === 'debt_discharge' ? '💳 Debt Discharge Workflow' :
                 '🛡️ Defendant Workflow'}
              </Badge>
              <span className="text-sm text-gray-500">(Position locked for this legal matter)</span>
            </div>
          </div>

          {/* Workflow Description - varies by case type */}
          {caseType === 'petitioner' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-5 mb-6 space-y-3" data-testid="workflow-description-petitioner">
              <h3 className="text-sm font-bold text-gray-900">Petitioner Workflow</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                As a <strong>Petitioner</strong>, you are bringing a claim before the court seeking relief, enforcement of your rights, or requesting specific equitable action. This workflow guides you through building a <strong>Petition grounded in Equity Jurisprudence</strong>, the Maxims of Equity, English Common Law, and the Magna Carta (1215). Your petition will establish standing, articulate the facts under oath, invoke the applicable maxims, and request a specific remedy the court is bound in conscience to grant.
              </p>
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">How to Use It:</h4>
                <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
                  <li>Enter your <strong>Client Information</strong> — your name, address, and jurisdiction</li>
                  <li>Identify the <strong>Opposing Party</strong> — the respondent, agency, or entity you are petitioning against</li>
                  <li>Select your <strong>legal category</strong> (e.g., property rights, family law, government overreach) and describe your case in detail</li>
                  <li>Upload any <strong>supporting documents</strong> — court orders, contracts, correspondence, or evidence of harm</li>
                  <li>Click <strong>"Generate Legal Remedy"</strong> — the AI will draft a formal Petition citing equitable maxims, establishing your standing, and requesting specific relief</li>
                  <li>Review the <strong>Petitioner's Equity Framework</strong> in the results to understand how to present your case in court with clean hands</li>
                </ol>
              </div>
            </div>
          )}

          {caseType === 'debt_discharge' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-6 space-y-3" data-testid="workflow-description-debt-discharge">
              <h3 className="text-sm font-bold text-gray-900">Debt Discharge Workflow</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                The <strong>Debt Discharge</strong> workflow is designed to help you challenge and resolve consumer debts, tax obligations, or other financial claims through the <strong>Conditional Acceptance process</strong>. Rather than refusing or ignoring a debt, you conditionally accept the claim — but only upon the creditor providing verified proof of claim, the original wet-ink contract, and evidence of lawful authority. If they fail to respond, their silence constitutes agreement that no valid claim exists, and the obligation is discharged through Equity.
              </p>
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">How to Use It:</h4>
                <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
                  <li>Enter your <strong>Client Information</strong> — your name, address, and jurisdiction</li>
                  <li>Identify the <strong>Creditor or Collector</strong> — the entity claiming you owe a debt</li>
                  <li>Select your <strong>debt category</strong> (e.g., consumer contract, tax collection, medical debt) and describe the alleged obligation</li>
                  <li>Upload any <strong>collection letters, statements, or notices</strong> you have received</li>
                  <li>Click <strong>"Generate Legal Remedy"</strong> — the AI will draft your Conditional Acceptance requiring proof of claim per Article I, Section 10, Clause 1</li>
                  <li>Follow the <strong>"Understanding the Debt Discharge Process"</strong> tab to learn the step-by-step mailing process for enforcing your remedy</li>
                </ol>
              </div>
            </div>
          )}

          {caseType === 'defendant' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6 space-y-3" data-testid="workflow-description-defendant">
              <h3 className="text-sm font-bold text-gray-900">Defendant Workflow</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                As a <strong>Defendant</strong>, you are responding to a claim, charge, or action brought against you. This workflow guides you through the <strong>Administrative Process</strong> — a private, lawful procedure to resolve disputes by requiring the opposing party to prove their claim with verified evidence. Through Conditional Acceptance, you shift the burden of proof onto the claimant. If they fail to respond with verified proof, their silence constitutes acquiescence and agreement that their claim has no merit.
              </p>
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">How to Use It:</h4>
                <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
                  <li>Enter your <strong>Client Information</strong> — your name, address, and jurisdiction</li>
                  <li>Identify the <strong>Opposing Party</strong> — the plaintiff, prosecutor, agency, or entity making a claim against you</li>
                  <li>Select your <strong>legal category</strong> (e.g., traffic, criminal charge, employment) and describe the charges or claims</li>
                  <li>Upload any <strong>court documents, citations, or notices</strong> you have received</li>
                  <li>Click <strong>"Generate Legal Remedy"</strong> — the AI will draft your Conditional Acceptance and administrative notice documents</li>
                  <li>Follow the <strong>"Understanding the Administrative Process"</strong> tab to learn the full mailing process for enforcing your remedy</li>
                </ol>
              </div>
            </div>
          )}

          {/* Petitioner Explainer Video - Only for Petitioner Cases */}
          {caseType === 'petitioner' && (
            <Card className="mb-6 border-2 border-purple-300 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100">
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <span className="text-2xl">🎥</span>
                  How to Use TheEqualizer.AI as a Petitioner
                </CardTitle>
                <p className="text-sm text-purple-700 mt-2">
                  Watch this quick tutorial to learn how to utilize the platform effectively for your case
                </p>
              </CardHeader>
              <CardContent className="p-4 bg-gray-50">
                <div className="relative w-full rounded-lg overflow-hidden shadow-md bg-black">
                  <video
                    className="w-full h-auto"
                    controls
                    preload="metadata"
                  >
                    <source 
                      src="https://customer-assets.emergentagent.com/job_equalizer-legal-4/artifacts/gh2xm3zj_Avatar%20IV%20Video%20%2814%29.mp4" 
                      type="video/mp4" 
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-800">
                    💡 <strong>Pro Tip:</strong> Watch this video before filling out your case information for the best experience.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Defendant Explainer Video - Only for Defendant Cases */}
          {caseType === 'defendant' && (
            <Card className="mb-6 border-2 border-blue-300 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100">
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <span className="text-2xl">🎥</span>
                  How to Use TheEqualizer.AI as a Defendant
                </CardTitle>
                <p className="text-sm text-blue-700 mt-2">
                  Watch this quick tutorial to learn how to utilize the platform effectively for your case
                </p>
              </CardHeader>
              <CardContent className="p-4 bg-gray-50">
                <div className="relative w-full rounded-lg overflow-hidden shadow-md bg-black">
                  <video
                    className="w-full h-auto"
                    controls
                    preload="metadata"
                  >
                    <source 
                      src="https://customer-assets.emergentagent.com/job_equalizer-legal-4/artifacts/wj8w4o5l_Avatar%20IV%20Video%20%2815%29.mp4" 
                      type="video/mp4" 
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Pro Tip:</strong> Watch this video before filling out your case information for the best experience.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 1: Client Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Step 1: Client Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Client Information */}
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <Input
                  type="text"
                  placeholder="Enter client's full name"
                  value={clientInfo.fullName}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, fullName: e.target.value }))}
                  className="w-full"
                />
              </div>

              {/* NEW: Country Dropdown */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country *
                </label>
                <Select
                  value={clientInfo.country}
                  onValueChange={(value) => 
                    setClientInfo(prev => ({ ...prev, country: value }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="United States">🇺🇸 United States</SelectItem>
                    <SelectItem value="The United Kingdom">🇬🇧 The United Kingdom</SelectItem>
                    <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                    <SelectItem value="Australia">🇦🇺 Australia</SelectItem>
                    <SelectItem value="Other">🌍 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <Input
                  type="text"
                  placeholder="Enter street address"
                  value={clientInfo.streetAddress}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, streetAddress: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <Input
                  type="text"
                  placeholder="Enter city"
                  value={clientInfo.city}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <Input
                  type="text"
                  placeholder="Enter state"
                  value={clientInfo.state}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, state: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP Code
                </label>
                <Input
                  type="text"
                  placeholder="Enter ZIP code"
                  value={clientInfo.zipCode}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, zipCode: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-gray-500">(Optional)</span>
                </label>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={clientInfo.email}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-gray-500">(Optional)</span>
                </label>
                <Input
                  type="tel"
                  placeholder="Enter phone number"
                  value={clientInfo.phone}
                  onChange={(e) => setClientInfo(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full"
                />
              </div>
            </div>
          </div>


          <div className="border-t pt-4">
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> All fields are optional. If left empty, placeholder values will be used in the generated documents that you can edit later.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Category Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            Step 2: {caseType === 'debt_discharge' ? 'Select Debt Type' : 'Select Legal Category'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full h-12 text-base">
              <SelectValue placeholder={caseType === 'debt_discharge' 
                ? "▼ Select Debt Type (Consumer Contract, Tax Collection)"
                : "▼ Select Legal Category (Traffic, Property, Contract...)"} />
            </SelectTrigger>
            <SelectContent>
              {legalCategories.map((category) => (
                <SelectItem key={category.value} value={category.value}>
                  <div className="flex items-center gap-2">
                    <span>{category.icon}</span>
                    <span>{category.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Step 3: Case Description */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Step 3: Describe Your Legal Matter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Describe your legal matter in natural language (who, what, when, where, why)..."
            value={caseDescription}
            onChange={(e) => setCaseDescription(e.target.value)}
            className="min-h-[120px] text-base"
          />
        </CardContent>
      </Card>

      {/* Step 2.5: Document Upload (Optional) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Step 4: Upload Documents for AI Analysis (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Upload relevant documents (contracts, notices, tickets, etc.) for the AI to analyze before generating your legal remedy.
              <span className="block mt-1 text-xs text-amber-700 font-medium">Note: It can take up to 30-45 seconds for a document to load into the system. BE AWARE The max document size is 50MB.</span>
            </p>
            
            {/* Upload Button */}
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-2 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">PDF, DOC, DOCX, TXT, JPG, PNG (Max 50MB)</p>
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  multiple 
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                />
              </label>
            </div>
            
            {/* Uploaded Documents List */}
            {uploadedDocuments.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Uploaded Documents:</h4>
                {uploadedDocuments.map((doc) => (
                  <div key={doc.id} className={`flex items-center justify-between p-3 rounded border ${doc.extractedText ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                    <div className="flex items-center gap-2">
                      <File className={`w-4 h-4 ${doc.extractedText ? 'text-green-600' : 'text-yellow-600'}`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-500">
                          {doc.size} {doc.extractedText
                            ? `- ${doc.extractedText.length.toLocaleString()} characters extracted for AI analysis`
                            : '- No extractable text (file stored)'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeDocument(doc.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Step 4: Generate Remedy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            Step 5: Generate Legal Remedy & Administrative Process
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Opposing Party Information for Remedy Generation */}
          <div className="p-4 bg-gray-50 rounded-lg border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-4 h-4" />
              Opposing Party Information (Optional)
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter the opposing party's information to automatically populate your legal documents:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opposing Party Name
                </label>
                <Input
                  type="text"
                  placeholder="Enter opposing party's full name (e.g., John Doe, ABC Company, City of Springfield)"
                  value={opposingPartyInfo.fullName}
                  onChange={(e) => setOpposingPartyInfo(prev => ({ ...prev, fullName: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opposing Party Address
                </label>
                <Input
                  type="text"
                  placeholder="Enter complete address (e.g., 123 Main St, Springfield, IL 62701)"
                  value={opposingPartyInfo.fullAddress}
                  onChange={(e) => setOpposingPartyInfo(prev => ({ ...prev, fullAddress: e.target.value }))}
                  className="w-full"
                />
              </div>
            </div>
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-xs text-yellow-800">
                <strong>Note:</strong> This information will be automatically inserted into your generated legal documents. If left empty, placeholder text will be used that you can edit later.
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleGenerateRemedy}
              disabled={isGenerating || !selectedCategory || !caseDescription.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                'Get Remedy'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 5: AI Remedy Results with Three Tabs */}
      {remedy && (
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-xl text-blue-900 mb-3">AI Remedy Results</CardTitle>
            
            {/* Instructions for using the remedy */}
            <div className="bg-white border-l-4 border-blue-600 p-4 rounded-r-lg shadow-sm">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <span className="text-lg">📋</span>
                How to Enforce Your Remedy:
              </h4>
              <ol className="text-sm text-blue-800 space-y-2 ml-6 list-decimal">
                <li>
                  Analyze the <strong>Case Matter Overview</strong> and <strong>"Understanding the Administrative Process"</strong> tabs below 
                  so you can know exactly how to enforce your remedy through the Administrative Process.
                </li>
                <li>
                  Then you need to <strong>scroll down</strong> and <strong>click on the other Case tabs</strong> 
                  (<strong>Enforcing Your Protections: First Mailing</strong>, <strong>Opportunity to Cure: Second Mailing</strong>, and <strong>Administrative Default: Third Mailing</strong>) 
                  to download the documents for your particular case matter.
                </li>
              </ol>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Tab 1: Case Matter Overview */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('legalOverview')}
                className="w-full p-4 text-left hover:bg-gray-50 flex items-center justify-between"
              >
                <span className="font-semibold text-gray-900">
                  {caseType === 'petitioner' ? '⚖️ Petitioner\'s Equity Framework' : '📋 Case Matter Overview'}
                </span>
                {expandedSections.legalOverview ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {expandedSections.legalOverview && (
                <div className="px-4 pb-4">
                  {caseType === 'petitioner' ? (
                    // Petitioner-specific Legal Matter Overview with Equity Framework
                    <div className="bg-purple-50 p-4 rounded text-sm max-h-96 overflow-y-auto">
                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-purple-800 mb-4">⚖️ PETITIONER'S EQUITY FRAMEWORK</h3>
                        <p className="text-purple-700 italic mb-4">
                          "Enforcing your equitable rights is not about asking permission—it's about showing the court that law, conscience, and equity compel them to grant remedy."
                        </p>
                        
                        <div className="space-y-4">
                          <div className="border-l-4 border-purple-600 pl-4">
                            <h4 className="font-semibold text-purple-800">1. Establish Standing in Equity</h4>
                            <p className="text-purple-700">
                              Declare yourself as the Petitioner <strong>in propria persona</strong> (in one's own person) or <strong>sui juris</strong> (of one's own right), not as a "legal fiction" or corporate entity.
                            </p>
                            <p className="text-purple-700 mt-2">
                              <strong>Invoke the Maxim:</strong> "Equity regards as done that which ought to be done." Frame the court as duty-bound to recognize your rights even if statutory law is silent.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-purple-600 pl-4">
                            <h4 className="font-semibold text-purple-800">2. Assert Foundational Authority</h4>
                            <p className="text-purple-700">
                              <strong>Magna Carta (1215):</strong> The king himself was bound by law; no one is above justice.<br/>
                              <strong>English Bill of Rights (1689):</strong> Guarantees against cruel punishment, unlawful fines, and arbitrary power.<br/>
                              <strong>U.S. Constitution & Bill of Rights:</strong> Article I Section 10 forbids impairment of contracts, ensures due process, and secures the right to petition.
                            </p>
                            <p className="text-purple-700 mt-2 font-medium">
                              "I stand under higher law—your statutes cannot override."
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-purple-600 pl-4">
                            <h4 className="font-semibold text-purple-800">3. Apply the Maxims of Equity</h4>
                            <p className="text-purple-700">
                              Bring the judge back to conscience:<br/>
                              • <strong>Equity acts in personam</strong> – It binds the person, not just the property<br/>
                              • <strong>Equity will not suffer a wrong without a remedy</strong> – If harm exists, remedy must exist<br/>
                              • <strong>He who comes to equity must come with clean hands</strong> – Establish you are in honor, the other side is in dishonor
                            </p>
                            <p className="text-purple-700 mt-2 italic">
                              Example: "Respondent has received benefit without providing lawful consideration. Equity abhors unjust enrichment."
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-purple-600 pl-4">
                            <h4 className="font-semibold text-purple-800">4. Conditional Acceptance & Default Process</h4>
                            <p className="text-purple-700">
                              • Accept all claims and charges conditionally upon proof of jurisdiction, authority, and injury<br/>
                              • Demand validation of debt, proof of claim, or proof of lawful authority<br/>
                              • When they fail (and they usually do), establish administrative default<br/>
                              • <strong>In equity, silence is acquiescence</strong>
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-purple-600 pl-4">
                            <h4 className="font-semibold text-purple-800">5. Demand Specific Remedy</h4>
                            <p className="text-purple-700">
                              Don't just argue. Tell the court what you want enforced:<br/>
                              • Discharge of debt<br/>
                              • Injunction against unlawful action<br/>
                              • Immediate release from unlawful detention (Habeas Corpus)<br/>
                              • Recognition of your Trust, Estate, or private rights
                            </p>
                            <p className="text-purple-700 mt-2 font-medium">
                              Backed by the Maxim: "Equity delights in doing complete justice."
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-purple-600 pl-4">
                            <h4 className="font-semibold text-purple-800">6. Document & Serve with Honor</h4>
                            <p className="text-purple-700">
                              • Every filing should be sent <strong>Certified Mail with return receipt</strong>—creating your audit trail of peaceful action<br/>
                              • Speak in affidavit form: sworn, under penalty of perjury, unrebutted = standing as truth<br/>
                              • "An unrebutted affidavit stands as truth in commerce and in equity"
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-purple-600 pl-4">
                            <h4 className="font-semibold text-purple-800">🛡️ 7. Stand Firm in Court</h4>
                            <p className="text-purple-700">
                              If the judge presses, you don't fold. Return to your equitable foundation:
                            </p>
                            <div className="bg-purple-100 p-3 rounded mt-2">
                              <p className="text-purple-800 font-medium italic">
                                "I am here in honor. I have conditionally accepted all claims. The opposing party has defaulted. In equity, default is confession. I now move this court for remedy."
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Default Defendant Case Matter Overview with highlighted user fields
                    <div 
                      className="bg-blue-50 p-4 rounded text-sm whitespace-pre-line max-h-96 overflow-y-auto"
                      dangerouslySetInnerHTML={renderRemedyWithHighlights(extractSection(remedy.fullResponse, 'SECTION 1'))}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Tab 2: Executing your Remedy - (Defendant and Debt Discharge) */}
            {(caseType === 'defendant' || caseType === 'debt_discharge') && (
              <div className="border-b">
                <button
                  onClick={() => toggleSection('executingRemedy')}
                  className="w-full p-4 text-left hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className="font-semibold text-gray-900">
                    {caseType === 'debt_discharge' ? '💳 Intro: Understanding the Debt Discharge Process' : '🛡️ Intro: Understanding the Administrative Process'}
                  </span>
                  {expandedSections.executingRemedy ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {expandedSections.executingRemedy && (
                  <div className="px-4 pb-4">
                    <div className="bg-blue-50 p-6 rounded-lg text-sm space-y-6">
                      
                      {/* What is the Administrative Process */}
                      <div className="border-l-4 border-blue-600 pl-4">
                        <h3 className="text-xl font-bold text-blue-900 mb-3">
                          {caseType === 'debt_discharge' ? 'What is the Debt Discharge Process?' : 'What is the Administrative Process?'}
                        </h3>
                        <p className="text-blue-800 leading-relaxed">
                          {caseType === 'debt_discharge' 
                            ? `The Debt Discharge Process is a lawful procedure to challenge and resolve ${selectedCategory === 'tax-collection' ? 'tax liabilities' : 'consumer debts'} through conditional acceptance. It requires the claiming party to prove their claim with evidence. If they fail to respond or provide proof, the matter is settled in your favor by default.`
                            : `The Administrative Process is a private, lawful procedure to resolve disputes outside of court. It allows you to respond to claims against you by requiring the opposing party to prove their claim with evidence. If they fail to respond or provide proof, you win by default.`
                          }
                        </p>
                        <p className="text-blue-800 leading-relaxed mt-2">
                          Think of it as creating an official record that says: <em>"I accept your claim — but only if you can prove it."</em> 
                          When they don't respond, their silence becomes <strong>agreement</strong> that they have no valid claim.
                        </p>
                      </div>

                      {/* Why is it Important */}
                      <div className="border-l-4 border-blue-600 pl-4">
                        <h3 className="text-xl font-bold text-blue-900 mb-3">
                          {caseType === 'debt_discharge' ? 'Why is Conditional Acceptance So Important?' : 'Why is the Administrative Process So Important?'}
                        </h3>
                        <div className="text-blue-800 space-y-2">
                          <p><strong>✓ Shifts the Burden of Proof:</strong> The {caseType === 'debt_discharge' ? 'creditor/collector' : 'opposing party'} must prove their claim is legitimate, not you proving it's not.</p>
                          <p><strong>✓ Creates an Official Record:</strong> Your documents create a paper trail showing you acted in honor and they didn't respond.</p>
                          <p><strong>✓ Establishes Default:</strong> Their failure to rebut = they admit your position is correct.</p>
                          <p><strong>✓ Avoids Court Drama:</strong> Settle matters privately before they escalate to expensive court battles.</p>
                          <p><strong>✓ Protects Your Rights:</strong> You're standing on solid legal ground using equity and common law principles.</p>
                        </div>
                      </div>

                      {/* Step-by-Step Process */}
                      <div className="border-l-4 border-blue-600 pl-4">
                        <h3 className="text-xl font-bold text-blue-900 mb-3">
                          {caseType === 'debt_discharge' ? 'The Debt Discharge Process: Step-by-Step' : 'The Administrative Process: Step-by-Step'}
                        </h3>
                        
                        <div className="space-y-4">
                          {/* Step 1 */}
                          <div className="bg-white p-4 rounded-lg shadow-sm">
                            <h4 className="font-bold text-blue-900 text-lg mb-2">📬 STEP 1: Send First Mailing Documents</h4>
                            <p className="text-blue-800 mb-2">
                              Go to <strong>Tab 3: Enforcing Your Protections - First Mailing</strong> and download all four documents:
                            </p>
                            <ul className="list-disc pl-6 text-blue-800 space-y-1">
                              <li><strong>Testimony Under God and Notice of Rescission</strong> - Your sworn testimony establishing clean hands and formally rescinding any presumed contracts lacking full disclosure or lawful consideration</li>
                              <li><strong>Affidavit of Status</strong> - Declaration of your standing as a living man/woman under Natural Law and Common Law protections</li>
                              <li><strong>Notice of Conditional Acceptance</strong> - You accept their claim IF they prove it with verified evidence under penalty of perjury</li>
                              <li><strong>Formal Demand for Accounting</strong> - Compels the opposing party to provide a full and complete accounting of all accounts, funds, assets, and transactions. Grounded in Equity Jurisprudence (fiduciary duty to account), English Common Law, Article I Section 10 Clause 1 (constitutional contract protection), and Magna Carta Chapter 29 (right to justice). Non-compliance within 30 days constitutes breach of fiduciary duty, impairment of contract obligation, and denial of justice</li>
                            </ul>
                            <p className="text-blue-800 mt-3 font-semibold">
                              ⚠️ Mail these documents via <strong>Certified Mail with Return Receipt</strong> to the opposing party, prosecutor, or agency.
                            </p>
                            <p className="text-blue-800 mt-2 text-sm">
                              Keep copies of everything and save the tracking numbers!
                            </p>
                          </div>

                          {/* Step 2 */}
                          <div className="bg-white p-4 rounded-lg shadow-sm">
                            <h4 className="font-bold text-blue-900 text-lg mb-2">⏳ STEP 2: Wait for Response (10-21 Days)</h4>
                            <p className="text-blue-800">
                              Give the opposing party <strong>10-21 days</strong> to respond with evidence proving their claim. 
                              They must provide:
                            </p>
                            <ul className="list-disc pl-6 text-blue-800 space-y-1 mt-2">
                              <li>Signed contracts with your wet-ink signature</li>
                              <li>Proof of harm or injury</li>
                              <li>Lawful authority to make their claim</li>
                            </ul>
                            <p className="text-blue-800 mt-3">
                              <strong>Most of the time, they won't respond.</strong> Why? Because they don't have proof!
                            </p>
                          </div>

                          {/* Step 3: Opportunity to Cure */}
                          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-amber-400">
                            <h4 className="font-bold text-amber-900 text-lg mb-2">⚖️ STEP 3: Opportunity to Cure (Second Mailing)</h4>
                            <p className="text-amber-800 mb-2">
                              If the {caseType === 'debt_discharge' ? 'creditor/collector' : 'opposing party'} <strong>fails to respond</strong> within the 21-day window, you must issue a <strong>Notice of Default and Opportunity to Cure</strong>. This is a critical step rooted in Equity Jurisprudence.
                            </p>
                            <p className="text-amber-800 mb-2">
                              The Opportunity to Cure gives the Respondent a <strong>final 10 calendar days</strong> to respond with verified evidence proving their claim. Under the maxim <em>"He who does not deny, admits"</em> (Qui non negat, fatetur), their continued silence perfects your administrative default and creates an irrebuttable presumption that all terms of your original Conditional Acceptance are accepted.
                            </p>
                            <ul className="list-disc pl-6 text-amber-800 space-y-1 mt-2">
                              <li>Demonstrates <strong>clean hands</strong> — you gave every fair opportunity to respond</li>
                              <li>Fulfills due process under the Magna Carta (1215), Chapter 40</li>
                              <li>Creates a binding administrative record of default</li>
                              <li>Authorizes you to proceed with the Certificate of Non-Response (Third Mailing)</li>
                            </ul>
                            <p className="text-amber-800 mt-3 font-semibold">
                              ⚠️ Go to <strong>Tab 4: Opportunity to Cure: Second Mailing</strong> to generate and download this document. Mail it via <strong>Certified Mail with Return Receipt</strong>.
                            </p>
                          </div>

                          {/* Step 4: If No Rebuttal - Third Mailing */}
                          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-orange-400">
                            <h4 className="font-bold text-orange-900 text-lg mb-2">📋 STEP 4: If No Rebuttal - Send Third Mailing (Administrative Default Documents)</h4>
                            <p className="text-orange-800 mb-2">
                              If they <strong>do NOT respond</strong> to the Opportunity to Cure or fail to provide proof, go to <strong>Tab 5: {caseType === 'debt_discharge' ? 'Debt Discharge: Third Mailing' : 'Administrative Default: Third Mailing'}</strong> and download these documents:
                            </p>
                            {caseType === 'debt_discharge' ? (
                              <ul className="list-disc pl-6 text-orange-800 space-y-1">
                                <li><strong>Certificate of Non-Response & Rescission</strong> - Declares they failed to respond</li>
                                <li><strong>Notice of Discharge Final</strong> - Discharges the alleged debt in equity</li>
                              </ul>
                            ) : (
                              <ul className="list-disc pl-6 text-orange-800 space-y-1">
                                <li><strong>Certificate of Non-Response & Rescission</strong> - Declares they failed to respond</li>
                                <li><strong>Notice of Discharge to Court</strong> - Notifies the court of administrative default</li>
                                <li><strong>Final Administrative Closure Notice</strong> - Officially closes the matter in your favor</li>
                              </ul>
                            )}
                            <p className="text-orange-800 mt-3 font-semibold">
                              ⚠️ Mail these to the {caseType === 'debt_discharge' ? 'creditor/collector' : 'opposing party AND the court'} via <strong>Certified Mail with Return Receipt</strong>.
                            </p>
                            <p className="text-orange-800 mt-2">
                              <strong>This puts them in DEFAULT.</strong> Their silence = agreement. You win.
                            </p>
                          </div>

                          {/* Step 5 */}
                          <div className="bg-white p-4 rounded-lg shadow-sm">
                            <h4 className="font-bold text-green-900 text-lg mb-2">✅ STEP 5: Matter Closed - You Stand in Honor</h4>
                            <p className="text-green-800">
                              Once you've completed the administrative process, <strong>the matter is settled</strong>. You have:
                            </p>
                            <ul className="list-disc pl-6 text-green-800 space-y-1 mt-2">
                              <li>Created an official record of their failure to respond</li>
                              <li>Established administrative default</li>
                              <li>Discharged the obligation through equity</li>
                              <li>Maintained "clean hands" by acting in honor</li>
                            </ul>
                            <p className="text-green-800 mt-3 font-semibold">
                              Keep all documents, receipts, and tracking info in a safe place. This is your proof.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* US-Only Constitutional Note */}
                      {clientInfo.country === "United States" && (
                        <div className="mt-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg">
                          <h4 className="font-semibold text-amber-900 mb-2">📜 Constitutional Foundation (U.S. Only)</h4>
                          <p className="text-amber-800 text-sm">
                            <strong>Article I, Section 10, Clause 1</strong> states: <em>"No State shall make any Thing but gold and silver Coin a Tender in Payment of Debts."</em>
                          </p>
                          <p className="text-amber-800 text-sm mt-2">
                            Since 1933, there's been no lawful way to "pay" debts in gold/silver. All modern "debts" are commercial obligations that must be 
                            <strong> discharged equitably</strong>. The Administrative Process is your constitutional remedy.
                          </p>
                        </div>
                      )}

                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab 3: Enforcing Your Protections: First Mailing */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('equitableRights')}
                className="w-full p-4 text-left hover:bg-gray-50 flex items-center justify-between"
              >
                <span className="font-semibold text-gray-900">
                  {caseType === 'petitioner' ? '📝 Drafting a Proper Petition' : 
                   caseType === 'debt_discharge' ? '💳 Debt Discharge: First Mailing' :
                   '⚖️ Enforcing Your Protections: First Mailing'}
                </span>
                {expandedSections.equitableRights ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {expandedSections.equitableRights && (
                <div className="px-4 pb-4">
                  {caseType === 'petitioner' ? (
                    // Petitioner-specific Drafting a Proper Petition content
                    <div className="bg-green-50 p-4 rounded text-sm max-h-96 overflow-y-auto mb-4">
                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-green-800 mb-4">📝 DRAFTING A PROPER PETITION</h3>
                        <p className="text-green-700 italic mb-4">
                          "A good petition in equity isn't just paper with words—it's a sword and shield in one. It tells the court: I stand in honor, I know my rights, and I am here to enforce them."
                        </p>
                        
                        <div className="space-y-4">
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">1. Proper Jurisdiction & Standing</h4>
                            <p className="text-green-700">
                              <strong>Identify yourself correctly</strong> – as the living man/woman, sui juris, not a corporate fiction.<br/>
                              <strong>Establish the forum</strong> – that equity applies because law alone offers no remedy.<br/>
                              <strong>Cite authority</strong> – Magna Carta, English Common Law, English Bill of Rights, U.S. Bill of Rights, and constitutional clauses (esp. Article I §10).
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">2. Statement of Facts (Affidavit Style)</h4>
                            <p className="text-green-700">
                              <strong>Lay out facts clearly</strong>, chronologically, and without unnecessary emotion.<br/>
                              <strong>Use numbered paragraphs</strong> – simple, irrefutable statements that stand unrebutted if unchallenged.<br/>
                              <strong>Swear the facts under penalty of perjury</strong> (an affidavit within the petition makes it binding).
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">3. Cause of Action in Equity</h4>
                            <p className="text-green-700">
                              <strong>Show that a wrong exists</strong> which law/statute alone cannot repair.<br/>
                              <strong>Tie your claim to a Maxim of Equity</strong> (e.g., "Equity will not suffer a wrong without a remedy").<br/>
                              <strong>Highlight dishonor, fraud, or unjust enrichment</strong> by the opposing party.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">4. Conditional Acceptance & Administrative Process</h4>
                            <p className="text-green-700">
                              <strong>Document that you gave the opposing party</strong> a chance to respond or cure, and they defaulted.<br/>
                              <strong>Show proof</strong> (mail receipts, notices, unrebutted affidavits) that they failed to rebut.<br/>
                              <strong>State that in equity</strong>, silence = acquiescence, and default = confession.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">5. Prayer for Relief (Remedy Demanded)</h4>
                            <p className="text-green-700">
                              <strong>Be precise. Don't just say "justice." Say:</strong><br/>
                              • "Discharge of alleged debt"<br/>
                              • "Immediate release from unlawful detention"<br/>
                              • "Permanent injunction against interference with private rights"<br/>
                              • "Recognition of private trust/estate"
                            </p>
                            <p className="text-green-700 mt-2 font-medium">
                              Use the Maxim: "Equity delights to do complete justice."
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">6. Format & Structure</h4>
                            <p className="text-green-700">
                              <strong>Title:</strong> "Petition in Equity for [Relief]"<br/>
                              <strong>Parties:</strong> Petitioner vs. Respondent<br/>
                              <strong>Sections:</strong> Introduction, Jurisdiction, Facts, Cause of Action, Remedy Sought, Verification<br/>
                              <strong>Verification block:</strong> "I, [Name], declare under penalty of perjury that the foregoing is true, correct, and complete to the best of my knowledge." Signed, dated, sealed.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">7. Honor & Clean Hands</h4>
                            <p className="text-green-700">
                              <strong>Petition must be respectful</strong>, never hostile or threatening.<br/>
                              <strong>Demonstrate that you came in honor</strong> (Maxim: "He who seeks equity must do equity.").<br/>
                              <strong>Show that you gave notice</strong>, opportunity to cure, and remained peaceful.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">8. Proof of Service & Record</h4>
                            <p className="text-green-700">
                              <strong>Always serve certified mail</strong> or with a process server.<br/>
                              <strong>Attach exhibits:</strong> contracts, affidavits, receipts, notices, prior correspondence.<br/>
                              <strong>Create an audit trail</strong>—your petition is only as strong as the record that backs it.
                            </p>
                          </div>
                        </div>
                        
                        <div className="mt-4 p-4 bg-green-100 border border-green-300 rounded">
                          <p className="text-sm text-green-800 font-medium">
                            In short: A good petition enforces equitable rights by being <strong>fact-based, affidavit-backed, maxim-grounded, remedy-focused, and honor-driven</strong>. It leaves the court no lawful escape but to grant remedy.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : caseType === 'debt_discharge' ? (
                    // Debt Discharge-specific content (no FOIA explainer)
                    <div className="bg-yellow-50 p-4 rounded text-sm max-h-96 overflow-y-auto mb-4">
                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-yellow-800 mb-4">💳 DEBT DISCHARGE PROCESS</h3>
                        
                        <div className="space-y-3">
                          <div className="border-l-4 border-yellow-600 pl-4">
                            <h4 className="font-semibold text-yellow-800">Step 1: Recognition</h4>
                            <p className="text-yellow-700">
                              Recognize the {selectedCategory === 'tax-collection' ? 'tax liability claim' : 'consumer debt claim'} as requiring verification under Common Law and Equity principles.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-yellow-600 pl-4">
                            <h4 className="font-semibold text-yellow-800">Step 2: Status Affirmation</h4>
                            <p className="text-yellow-700">
                              Provide an Affidavit of Status to affirm your standing as a living man/woman, distinct from any corporate legal fiction.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-yellow-600 pl-4">
                            <h4 className="font-semibold text-yellow-800">Step 3: Conditional Acceptance (Non-Dispute)</h4>
                            <p className="text-yellow-700">
                              Submit a Conditional Acceptance stating you <strong>DO NOT REFUSE</strong> their claim, but conditionally accept only upon strict proof of claim and lawful payment instructions per Article I, Section 10, Clause 1.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-yellow-600 pl-4">
                            <h4 className="font-semibold text-yellow-800">Step 4: Lawful Payment Inquiry</h4>
                            <p className="text-yellow-700">
                              Demand clarification on how to lawfully pay in constitutional gold and silver coin, as required by Article I, Section 10, Clause 1.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-amber-600 pl-4">
                            <h4 className="font-semibold text-amber-800">Step 5: Demand for Accounting</h4>
                            <p className="text-amber-700">
                              Submit a Formal Demand for Accounting to compel a <strong>full and complete accounting</strong> of all accounts, funds, assets, and transactions. Under Equity Jurisprudence, fiduciaries and trustees have an inherent obligation to render a true account. Non-compliance within 30 days constitutes breach of fiduciary duty and denial of justice under the Magna Carta.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-yellow-600 pl-4">
                            <h4 className="font-semibold text-yellow-800">Step 6: Certified Mail & 21-Day Response</h4>
                            <p className="text-yellow-700">
                              Mail your documents via Certified Mail and allow 21 days for response. Their silence = acquiescence to discharge.
                            </p>
                          </div>
                        </div>
                        
                        <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded">
                          <p className="text-xs text-yellow-800">
                            <strong>Important:</strong> This is conditional acceptance, not refusal. You stand in honor, willing to settle any lawful, verified obligation upon proof of claim and constitutional payment method.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Default Defendant Enforce Your Equitable Rights content
                    <div className="bg-green-50 p-4 rounded text-sm max-h-96 overflow-y-auto mb-4">
                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-green-800 mb-4">ENFORCE YOUR EQUITABLE RIGHTS</h3>
                        
                        <div className="space-y-3">
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">Step 1: Recognition</h4>
                            <p className="text-green-700">
                              Recognize the {selectedCategory || 'legal matter'} as a statutory charge that requires a lawful review under Common Law principles.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">Step 2: Status Affirmation</h4>
                            <p className="text-green-700">
                              Provide an Affidavit of Status to affirm your standing as a living man/woman, distinct from any corporate legal fiction.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">Step 3: Testimony Under God and Notice of Rescission</h4>
                            <p className="text-green-700">
                              Submit a sworn testimony under penalty of perjury combined with formal rescission of any presumed contracts or consent{clientInfo.country === "United States" ? ". This document invokes your rights under Article I Section 10 Clause 1" : " based on equity principles and common law"}.
                            </p>
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                              <h6 className="font-semibold text-green-800 mb-2">Why Testimony Under God and Notice of Rescission is Critical in Equity Jurisprudence:</h6>
                              <ul className="text-sm text-green-700 space-y-1">
                                <li>• <strong>Equity Foundation:</strong> Establishes clean hands doctrine - you come to equity with transparency and honesty</li>
                                <li>• <strong>Rescission Power:</strong> Equity allows rescission of contracts formed without full disclosure, meeting of minds, or under duress</li>
                                <li>• <strong>Maxim Application:</strong> "Equity will not suffer a wrong without a remedy" - provides remedy for contracts lacking consideration</li>
                                <li>• <strong>Burden Shifting:</strong> Forces opposing party to prove lawful authority and valid contract formation</li>
                                <li>• <strong>Constitutional Standing:</strong> Affirms your status under Common Law rather than statutory presumptions</li>
                                <li>• <strong>Notice Requirement:</strong> Satisfies equity's requirement for proper notice before enforcement actions</li>
                                <li>• <strong>Good Faith Demonstration:</strong> Shows willingness to perform if lawful obligation exists (conditional acceptance)</li>
                              </ul>
                            </div>
                          </div>
                          
                          <div className="border-l-4 border-blue-600 pl-4">
                            <h4 className="font-semibold text-blue-800">
                              {clientInfo.country === "Canada" 
                                ? "Step 4: Access to Information Act Request (Canada)" 
                                : clientInfo.country === "The United Kingdom"
                                ? "Step 4: Freedom of Information Request (UK)"
                                : clientInfo.country === "Australia"
                                ? "Step 4: Freedom of Information Act 1982 Request (Australia)"
                                : "Step 4: FOIA Request (Freedom of Information Act)"}
                            </h4>
                            <p className="text-blue-700">
                              {clientInfo.country === "Canada"
                                ? "Submit an Access to Information Act request to verify that all acting officials have met constitutional requirements. This demonstrates the principle of accountability - all government officers must show lawful authority under Canadian law."
                                : clientInfo.country === "Australia"
                                ? "Submit a Freedom of Information Act 1982 request to verify that all acting officials have met their obligations. This demonstrates the principle of accountability - all Commonwealth officers must show lawful authority under Australian law."
                                : "Submit a Public Records Request to verify that all acting officials have met constitutional requirements. This demonstrates the principle of accountability - all government officers must show lawful authority."}
                            </p>
                            <div className="mt-3 space-y-2">
                              <div className="flex items-center justify-between bg-blue-50 p-3 rounded border border-blue-200">
                                <div>
                                  <h5 className="font-medium text-blue-900">
                                    {clientInfo.country === "Canada" 
                                      ? "Download Access to Information Request" 
                                      : clientInfo.country === "The United Kingdom"
                                      ? "Download UK FOIA Request"
                                      : clientInfo.country === "Australia"
                                      ? "Download Australian FOI Request"
                                      : "Download FOIA Request"}
                                  </h5>
                                  <p className="text-sm text-blue-700">Personalized Word document with your case information</p>
                                </div>
                                <Button
                                  onClick={handleDownloadFOIARequest}
                                  variant="outline"
                                  size="sm"
                                  className="bg-blue-600 text-white hover:bg-blue-700"
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  {clientInfo.country === "Canada" 
                                    ? "Download ATIA" 
                                    : clientInfo.country === "Australia"
                                    ? "Download FOI"
                                    : "Download FOIA"}
                                </Button>
                              </div>
                            </div>
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                              <h6 className="font-semibold text-blue-800 mb-2">
                                {clientInfo.country === "Canada" 
                                  ? "Why Access to Information Act is Critical in Your Case:" 
                                  : "Why FOIA is Critical in Your Case:"}
                              </h6>
                              <ul className="text-sm text-blue-700 space-y-1">
                                <li>• <strong>Accountability:</strong> Forces officials to prove their lawful authority</li>
                                <li>• <strong>Due Process:</strong> Constitutional right to verify official qualifications</li>
                                <li>• <strong>Evidence:</strong> Creates official record for court proceedings</li>
                                <li>• <strong>Burden of Proof:</strong> Shifts burden to officials to prove authority</li>
                                <li>• <strong>Constitutional Compliance:</strong> Verifies Article VI, Clause 3 oath requirements</li>
                              </ul>
                            </div>
                            {/* FARA Act Section - Only for US users */}
                            {clientInfo.country !== "Canada" && (
                              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                <h6 className="font-semibold text-yellow-800 mb-2">FARA Act (Foreign Agent Registration Act) Importance:</h6>
                                <ul className="text-sm text-yellow-700 space-y-1">
                                  <li>• <strong>Foreign Agent Detection:</strong> Identifies if public agents are acting as foreign agents</li>
                                  <li>• <strong>Unclean Hands Doctrine:</strong> Exposes officials attempting to persecute Americans while acting as foreign agents</li>
                                  <li>• <strong>Jurisdictional Challenge:</strong> Questions authority of foreign-influenced officials</li>
                                  <li>• <strong>Constitutional Protection:</strong> Protects against foreign interference in American legal proceedings</li>
                                </ul>
                              </div>
                            )}
                          </div>
                          
                          {/* Step 4b: UK-Specific Equitable Objection - ONLY for UK users */}
                          {clientInfo.country === "The United Kingdom" && (
                            <div className="border-l-4 border-purple-600 pl-4">
                              <h4 className="font-semibold text-purple-800">Step 4b: Notice of Equitable Objection & Demand for Clarification (UK ONLY)</h4>
                              <p className="text-purple-700">
                                For UK users: This document challenges BAR agents, solicitors, and barristers by demanding they clarify whether they act under the <strong>de jure Parliament of England</strong> (bound by Magna Carta and Bill of Rights 1689) or the <strong>de facto UK Parliament</strong> (corporate creation via unions of 1707 and 1801).
                              </p>
                              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded">
                                <h6 className="font-semibold text-purple-800 mb-2">Why This UK Document is Critical:</h6>
                                <ul className="text-sm text-purple-700 space-y-1">
                                  <li>• <strong>Burden Shifting:</strong> Forces BAR agents to prove their de jure authority</li>
                                  <li>• <strong>Foreign Agency Exposure:</strong> Frames BAR agents as potentially foreign (corporate allegiance)</li>
                                  <li>• <strong>Equity Protection:</strong> Invokes Magna Carta, Petition of Right 1628, Bill of Rights 1689</li>
                                  <li>• <strong>14-Day Demand:</strong> Requires written clarification within 14 days</li>
                                  <li>• <strong>Honor Maintained:</strong> You seek settlement in equity, not conflict</li>
                                  <li>• <strong>National Security Act 2023:</strong> References modern foreign influence concerns</li>
                                </ul>
                              </div>
                              <div className="mt-3 p-3 bg-purple-100 border border-purple-300 rounded">
                                <p className="text-xs text-purple-800">
                                  <strong>Note:</strong> This document is automatically included in your generated documents below when you select UK as your country.
                                </p>
                              </div>
                            </div>
                          )}
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">Step 5: Conditional Acceptance</h4>
                            <p className="text-green-700">
                              Submit a Conditional Acceptance because this document makes it clear you <strong>DO NOT REFUSE</strong> their claim (avoiding dishonor), but you conditionally accept only if they can prove lawful, full disclosure{clientInfo.country === "United States" ? ", and a Lawful way to pay according to Article 1 Section 10 Clause 1" : " and equitable settlement terms that avoid unjust enrichment"}.
                            </p>
                          </div>
                          
                          <div className="border-l-4 border-amber-600 pl-4">
                            <h4 className="font-semibold text-amber-800">Step 6: Demand for Accounting</h4>
                            <p className="text-amber-700">
                              Submit a Formal Demand for Accounting to compel the opposing party to provide a <strong>full and complete accounting</strong> of all accounts, funds, assets, transactions, and obligations. This demand is grounded in:
                            </p>
                            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
                              <h6 className="font-semibold text-amber-800 mb-2">Why the Demand for Accounting is Critical:</h6>
                              <ul className="text-sm text-amber-700 space-y-1">
                                <li>• <strong>Equity Jurisprudence:</strong> Courts of Equity have inherent jurisdiction to compel accounting wherever a fiduciary or trust relationship exists ("Equity will not suffer a wrong without a remedy")</li>
                                <li>• <strong>English Common Law:</strong> Fiduciaries, agents, partners, and trustees have an inherent obligation to render a true and complete account</li>
                                <li>• <strong>Article I, Section 10, Clause 1:</strong> The fiduciary/contractual relationship is constitutionally protected — any attempt to impair the right to an accounting is void</li>
                                <li>• <strong>Magna Carta (Chapter 29):</strong> "To no one will we sell, to no one will we deny or delay, right or justice" — failure to account is a denial of justice</li>
                                <li>• <strong>Pre-Suit Requirement:</strong> Establishes a formal record of demand — a prerequisite for seeking judicial relief if the Respondent fails to comply within 30 days</li>
                                <li>• <strong>Burden Shifting:</strong> Places the burden squarely on the Respondent to produce all financial records, contracts, and documentation</li>
                              </ul>
                            </div>
                          </div>
                          
                          <div className="border-l-4 border-green-600 pl-4">
                            <h4 className="font-semibold text-green-800">Step 7: Certified Mail</h4>
                            <p className="text-green-700">
                              Mail your documents via Certified Mail with a Green return slip and wait for a response.
                            </p>
                          </div>
                        </div>
                        
                        {/* US-Only Disclosure - Article I Section 10 */}
                        {clientInfo.country === "United States" && (
                          <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded">
                            <p className="text-xs text-green-800">
                              <strong>Important:</strong> All documents must specify payment in lawful money only (constitutional gold and silver coin) per Article I Section 10 Clause 1.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Petition Download Section - Only for Petitioners */}
                  {caseType === 'petitioner' && (
                    <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="text-lg font-semibold text-purple-900 mb-3 flex items-center gap-2">
                        <FileDown className="w-5 h-5" />
                        Download Your Custom Petition
                      </h4>
                      <p className="text-sm text-purple-700 mb-4">
                        Generate and download a personalized petition document based on your case information and selected category:
                      </p>
                      
                      <div className="text-center">
                        <Button
                          onClick={handleDownloadPetition}
                          disabled={!selectedCategory || !caseDescription.trim()}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 font-semibold rounded-lg"
                        >
                          <FileDown className="mr-2 w-4 h-4" />
                          Download Petition (.docx)
                        </Button>
                      </div>
                      
                      <div className="mt-4 p-3 bg-purple-100 border border-purple-300 rounded">
                        <p className="text-xs text-purple-800">
                          <strong>Your petition will include:</strong> Proper jurisdiction & standing, statement of facts, cause of action in equity, conditional acceptance, prayer for relief, format & structure, honor & clean hands, and proof of service sections - all customized to your case.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Document Generation Section - Only for Defendants */}
                  {caseType === 'defendant' && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        <Gavel className="w-5 h-5" />
                        Generate Legal Documents
                      </h4>
                      <p className="text-sm text-blue-700 mb-4">
                        Generate and download the four essential legal documents based on your case information:
                      </p>
                      
                      {!generatedDocuments ? (
                        <div className="text-center">
                          <Button
                            onClick={handleGenerateDocuments}
                            disabled={isGeneratingDocuments}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 font-semibold rounded-lg"
                          >
                            {isGeneratingDocuments ? (
                              <>
                                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                Generating Documents...
                              </>
                            ) : (
                              <>
                                <FileDown className="mr-2 w-4 h-4" />
                                Generate Legal Documents
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-green-700 font-medium">✅ Documents Generated Successfully!</p>
                          
                          {/* Document Download Buttons */}
                          <div className="grid gap-3">
                            {(() => {
                              const testimonyKey = clientInfo.country === "The United Kingdom" ? 'testimony_under_god_uk' : clientInfo.country === "Canada" ? 'testimony_under_god_canada' : clientInfo.country === "Australia" ? 'testimony_under_god_australia' : 'testimony_under_god_rescission';
                              const affidavitKey = clientInfo.country === "The United Kingdom" ? 'affidavit_of_status_uk' : clientInfo.country === "Canada" ? 'affidavit_of_status_canada' : clientInfo.country === "Australia" ? 'affidavit_of_status_australia' : 'affidavit_of_status';
                              const conditionalKey = clientInfo.country === "The United Kingdom" ? 'conditional_acceptance_uk' : clientInfo.country === "Canada" ? 'conditional_acceptance_canada' : clientInfo.country === "Australia" ? 'conditional_acceptance_australia' : 'notice_of_conditional_acceptance';

                              const docCards = [
                                { key: testimonyKey, num: 1, title: 'Testimony Under God and Notice of Rescission', desc: 'Sworn testimony with formal rescission notice invoking equitable jurisdiction and constitutional protections', filename: 'Testimony_Under_God_and_Notice_of_Rescission.docx' },
                                { key: affidavitKey, num: 2, title: 'Affidavit of Status', desc: 'Declaration of standing as Private National under Natural Law and Common Law protections', filename: 'Affidavit_of_Status.docx' },
                                { key: conditionalKey, num: 3, title: 'Notice of Conditional Acceptance', desc: 'Conditional acceptance for value upon proof of lawful claim and full disclosure', filename: 'Notice_of_Conditional_Acceptance.docx' },
                                { key: 'demand_for_accounting', num: 4, title: 'Formal Demand for Accounting', desc: 'Demands full accounting of all funds, assets, and transactions grounded in Equity Jurisprudence, English Common Law, Article I Section 10 Clause 1, and the Magna Carta', filename: 'Formal_Demand_for_Accounting.docx', testid: 'demand-for-accounting-download' },
                              ];

                              return docCards.map(({ key, num, title, desc, filename, testid }) => {
                                const refused = isDocumentRefused(key);
                                return (
                                  <div key={key} className={`flex items-start justify-between p-3 bg-white rounded border ${refused ? 'border-amber-300 bg-amber-50' : 'border-blue-200'}`} data-testid={testid || undefined}>
                                    <div className="flex-1 mr-3">
                                      <h5 className={`font-medium ${refused ? 'text-amber-900' : 'text-gray-900'}`}>{num}. {title}</h5>
                                      <p className="text-xs text-gray-600">{desc}</p>
                                      {refused && (
                                        <div className="mt-2 p-2 bg-amber-100 border border-amber-200 rounded" data-testid={`doc-not-applicable-${key}`}>
                                          <p className="text-xs font-semibold text-amber-800 flex items-center gap-1 mb-1">
                                            <AlertTriangle className="w-3 h-3" /> Not Applicable to Your Case
                                          </p>
                                          <p className="text-xs text-amber-700">{getRefusalReason(key)}</p>
                                        </div>
                                      )}
                                    </div>
                                    {!refused ? (
                                      <Button
                                        onClick={() => handleDownloadDocument(key, filename)}
                                        variant="outline"
                                        size="sm"
                                        className="bg-blue-600 text-white hover:bg-blue-700 flex-shrink-0"
                                      >
                                        <Download className="w-4 h-4 mr-1" />
                                        Download
                                      </Button>
                                    ) : (
                                      <Badge variant="outline" className="border-amber-400 text-amber-700 text-xs flex-shrink-0">
                                        Not Needed
                                      </Badge>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                            
                            {/* Document 5: Notice of Equitable Objection - UK ONLY */}
                            {clientInfo.country === "The United Kingdom" && generatedDocuments['equitable_objection_uk'] && (
                              <div className="flex items-center justify-between p-3 bg-white rounded border border-purple-200">
                                <div>
                                  <h5 className="font-medium text-gray-900">5b. Notice of Equitable Objection & Demand for Clarification (UK)</h5>
                                  <p className="text-xs text-gray-600">UK-specific objection challenging BAR agents' authority under de jure vs de facto Parliament distinction</p>
                                </div>
                                <Button
                                  onClick={() => handleDownloadDocument('equitable_objection_uk', 'Notice_of_Equitable_Objection_UK.docx')}
                                  variant="outline"
                                  size="sm"
                                  className="bg-purple-600 text-white hover:bg-purple-700"
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Download
                                </Button>
                              </div>
                            )}
                          </div>
                          
                          {/* US-Only Disclosure - Article I Section 10 and Federal Reserve Notes */}
                          {clientInfo.country === "United States" && (
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                              <p className="text-xs text-amber-800">
                                <strong>Important:</strong> All documents include lawful money requirements (constitutional gold and silver coin only) 
                                and reject Federal Reserve Notes per Article I Section 10 Clause 1.
                              </p>
                            </div>
                          )}
                          
                          <div className="text-center mt-4">
                            <Button
                              onClick={handleGenerateDocuments}
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Regenerate Documents
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Document Generation Section - Only for Debt Discharge */}
                  {caseType === 'debt_discharge' && (
                    <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <h4 className="text-lg font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                        <Gavel className="w-5 h-5" />
                        Debt Discharge Documents - First Mailing
                      </h4>
                      <p className="text-sm text-yellow-700 mb-4">
                        Download the following documents to begin your {selectedCategory === 'tax-collection' ? 'tax liability' : 'consumer debt'} discharge process:
                      </p>
                      
                      {!generatedDebtDischargeDocuments ? (
                        <div className="text-center">
                          <Button
                            onClick={handleGenerateDebtDischargeDocuments}
                            disabled={isGeneratingDebtDischargeDocuments}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 font-semibold rounded-lg"
                          >
                            {isGeneratingDebtDischargeDocuments ? (
                              <>
                                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                Generating Documents...
                              </>
                            ) : (
                              <>
                                <FileDown className="mr-2 w-4 h-4" />
                                Generate Debt Discharge Documents
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-green-700 font-medium">✅ Documents Generated Successfully!</p>
                          
                          {/* Debt Discharge Document Download Buttons */}
                          <div className="grid gap-3">
                            {/* Document 1: Affidavit of Status (reusing existing) */}
                            <div className="flex items-center justify-between p-3 bg-white rounded border border-yellow-200">
                              <div>
                                <h5 className="font-medium text-gray-900">1. Affidavit of Status</h5>
                                <p className="text-xs text-gray-600">Declaration of standing as a living man/woman under Natural Law and Common Law protections</p>
                              </div>
                              <Button
                                onClick={() => handleDownloadDebtDischargeDocument('affidavit_of_status', 'Affidavit_of_Status.docx')}
                                variant="outline"
                                size="sm"
                                className="bg-yellow-600 text-white hover:bg-yellow-700"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                            </div>
                            
                            {/* Document 2: Testimony Under God (reusing existing) */}
                            <div className="flex items-center justify-between p-3 bg-white rounded border border-yellow-200">
                              <div>
                                <h5 className="font-medium text-gray-900">2. Testimony Under God</h5>
                                <p className="text-xs text-gray-600">Sworn testimony invoking equitable jurisdiction and constitutional protections</p>
                              </div>
                              <Button
                                onClick={() => handleDownloadDebtDischargeDocument('testimony_under_god_rescission', 'Testimony_Under_God.docx')}
                                variant="outline"
                                size="sm"
                                className="bg-yellow-600 text-white hover:bg-yellow-700"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                            </div>
                            
                            {/* Document 3: Notice of Conditional Acceptance (Category specific) */}
                            <div className="flex items-center justify-between p-3 bg-white rounded border-2 border-yellow-400">
                              <div>
                                <h5 className="font-medium text-gray-900">
                                  3. Notice of Conditional Acceptance 
                                  <Badge className="ml-2 bg-yellow-100 text-yellow-800">
                                    {selectedCategory === 'tax-collection' ? 'Tax Collection' : 'Consumer Contract'}
                                  </Badge>
                                </h5>
                                <p className="text-xs text-gray-600">
                                  {selectedCategory === 'tax-collection' 
                                    ? 'Conditional acceptance for tax collection agencies with lawful payment inquiry'
                                    : 'Conditional acceptance for consumer debt collectors with proof of claim requirements'}
                                </p>
                              </div>
                              <Button
                                onClick={handleDownloadDebtDischargeConditionalAcceptance}
                                variant="outline"
                                size="sm"
                                className="bg-yellow-600 text-white hover:bg-yellow-700"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                            </div>
                            
                            {/* Document 4: Demand for Accounting */}
                            <div className="flex items-center justify-between p-3 bg-white rounded border border-yellow-200" data-testid="debt-demand-for-accounting-download">
                              <div>
                                <h5 className="font-medium text-gray-900">4. Formal Demand for Accounting</h5>
                                <p className="text-xs text-gray-600">Demands full accounting of all funds, assets, and transactions grounded in Equity Jurisprudence, English Common Law, Article I Section 10 Clause 1, and the Magna Carta</p>
                              </div>
                              <Button
                                onClick={() => handleDownloadDebtDischargeDocument('demand_for_accounting', 'Formal_Demand_for_Accounting.docx')}
                                variant="outline"
                                size="sm"
                                className="bg-yellow-600 text-white hover:bg-yellow-700"
                                data-testid="debt-download-demand-for-accounting-btn"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                            </div>
                          </div>
                          
                          {/* Debt Discharge specific note */}
                          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                            <p className="text-xs text-amber-800">
                              <strong>Important:</strong> The Notice of Conditional Acceptance is specifically tailored for your 
                              {selectedCategory === 'tax-collection' ? ' tax collection ' : ' consumer contract '} 
                              matter. This document establishes your position in honor while requiring proof of claim per Article I Section 10 Clause 1.
                            </p>
                          </div>
                          
                          <div className="text-center mt-4">
                            <Button
                              onClick={handleGenerateDebtDischargeDocuments}
                              variant="outline"
                              size="sm"
                              className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Regenerate Documents
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Mailing Tracker for First Mailing */}
                  {caseType !== 'petitioner' && (
                    <MailingTracker
                      conversationId={conversationId}
                      mailingStep="first_mailing"
                      stepLabel="First Mailing"
                      linkedDocuments={['testimony_under_god_rescission', 'affidavit_of_status', 'notice_of_conditional_acceptance', 'demand_for_accounting']}
                      onSwitchToStrategy={onSwitchToStrategy}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Tab 3.5: Opportunity to Cure: Second Mailing */}
            {(caseType === 'defendant' || caseType === 'debt_discharge') && (
              <div>
                <button
                  onClick={() => toggleSection('opportunityToCure')}
                  className="w-full p-4 text-left hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className="font-bold text-gray-900">
                    ⚖️ Opportunity to Cure: Second Mailing
                  </span>
                  {expandedSections.opportunityToCure ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {expandedSections.opportunityToCure && (
                  <div className="p-4 border-t space-y-4">
                    {/* Explanation */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                      <h4 className="font-bold text-amber-900 text-base">What is an Opportunity to Cure?</h4>
                      <p className="text-sm text-gray-700">
                        An <strong>Opportunity to Cure</strong> is a formal Notice of Fault issued after the opposing party fails to respond to your initial Conditional Acceptance within the prescribed response window (typically 21 days). It is the <strong>second step</strong> in the administrative remedy process under Equity Jurisprudence.
                      </p>
                      <p className="text-sm text-gray-700">
                        Under the equitable maxim <em>"He who does not deny, admits,"</em> the opposing party's silence constitutes a tacit admission of all facts in your original notice. However, Equity requires that you give them one final, good-faith opportunity to cure their default before you proceed to administrative closure. This is rooted in the principle <em>"Equity aids the vigilant, not the indolent"</em> and demonstrates that you have acted with clean hands throughout the process.
                      </p>
                      <p className="text-sm text-gray-700">
                        The Opportunity to Cure demands that the opposing party provide verified proof of claim, the original wet-ink contract, evidence of authority, and proof of lawful consideration under Article I, Section 10, Clause 1 within <strong>10 calendar days</strong>. If they again fail to respond, their silence perfects your administrative record and authorizes you to proceed with issuing a Certificate of Non-Response (Third Mailing).
                      </p>
                      <div className="bg-white border border-amber-300 rounded p-3 mt-2">
                        <p className="text-xs text-amber-800 font-semibold mb-1">Why This Step Matters:</p>
                        <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
                          <li>Establishes that you gave the opposing party every fair opportunity to respond, fulfilling due process under the Magna Carta (1215), Chapter 40</li>
                          <li>Creates an irrebuttable presumption of default if no response is received</li>
                          <li>Demonstrates clean hands in Equity, which is essential for any future remedy or relief</li>
                          <li>Perfects the administrative record for the Certificate of Non-Response</li>
                        </ul>
                      </div>
                    </div>

                    {/* Respondent Response Upload - Second Mailing */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="response-upload-second-mailing">
                      <h4 className="font-semibold text-blue-900 text-sm flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Did the Respondent Reply? (Optional)
                      </h4>
                      <p className="text-xs text-gray-600">
                        If you received a response from the opposing party to your First Mailing, upload it here. The AI will analyze their response and address their arguments directly in the Opportunity to Cure document.
                      </p>
                      {!respondentResponse2 ? (
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors text-sm font-medium text-blue-700">
                            <Upload className="w-4 h-4" />
                            Upload Response
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={(e) => handleResponseUpload(e, 'second_mailing')}
                              disabled={isUploadingResponse}
                              data-testid="response-upload-input-second"
                            />
                          </label>
                          {isUploadingResponse && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                          <span className="text-xs text-gray-500">PDF, DOCX, TXT, JPG, PNG (max 50MB)</span>
                        </div>
                      ) : (
                        <div className="bg-white border border-blue-200 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <File className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-900">{respondentResponse2.name}</span>
                              <span className="text-xs text-gray-500">({(respondentResponse2.size / 1024).toFixed(1)} KB)</span>
                            </div>
                            <button onClick={() => removeRespondentResponse('second_mailing')} className="text-gray-400 hover:text-red-500 transition-colors" data-testid="remove-response-second">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          {respondentResponse2.extractedText && (
                            <div className="bg-gray-50 rounded p-2 max-h-32 overflow-y-auto">
                              <p className="text-xs text-gray-500 font-medium mb-1">Extracted Text Preview:</p>
                              <p className="text-xs text-gray-700 whitespace-pre-wrap">{respondentResponse2.extractedText.substring(0, 500)}{respondentResponse2.extractedText.length > 500 ? '...' : ''}</p>
                            </div>
                          )}
                          <p className="text-xs text-green-700 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Response will be addressed in the generated document
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Generate / Download */}
                    {!generatedOpportunityCureDoc ? (
                      <div className="text-center space-y-3 py-4">
                        <p className="text-sm text-gray-600">Generate your Opportunity to Cure document based on your case details.</p>
                        <Button
                          onClick={async () => {
                            setIsGeneratingOpportunityCure(true);
                            try {
                              const token = localStorage.getItem('token');
                              const caseData = {
                                full_name: clientInfo?.fullName || '[Your Name]',
                                case_description: caseDescription || '',
                                category: selectedCategory || 'general',
                                state: clientInfo?.state || '',
                                address: clientInfo?.address || '',
                                city: clientInfo?.city || '',
                                zip_code: clientInfo?.zipCode || '',
                                opposing_party: opposingPartyInfo?.fullName || '[Opposing Party]',
                                opposing_address: `${opposingPartyInfo?.address || ''} ${opposingPartyInfo?.city || ''} ${opposingPartyInfo?.state || ''} ${opposingPartyInfo?.zipCode || ''}`.trim(),
                                country: clientInfo?.country || 'United States',
                                respondent_response: respondentResponse2?.extractedText || '',
                              };
                              const res = await axios.post(
                                `${API_BASE}/conversations/${conversationId}/generate-opportunity-cure`,
                                caseData,
                                { headers: { 'Authorization': `Bearer ${token}` } }
                              );
                              if (res.data.success && res.data.job_id) {
                                const jobId = res.data.job_id;
                                toast({
                                  title: "Generating Document",
                                  description: "Claude Sonnet 4.6 is drafting your Opportunity to Cure. This may take 30-60 seconds...",
                                });

                                let attempts = 0;
                                const maxAttempts = 60;
                                const pollInterval = 3000;

                                while (attempts < maxAttempts) {
                                  attempts++;
                                  await new Promise(r => setTimeout(r, pollInterval));
                                  try {
                                    const pollRes = await axios.get(
                                      `${API_BASE}/conversations/${conversationId}/document-job/${jobId}`,
                                      { headers: { 'Authorization': `Bearer ${token}` } }
                                    );
                                    if (pollRes.data.status === 'completed' && pollRes.data.document) {
                                      setGeneratedOpportunityCureDoc(pollRes.data.document);
                                      try {
                                        const convRes = await axios.get(`${API_BASE}/conversations/${conversationId}`, { headers: { 'Authorization': `Bearer ${token}` } });
                                        const existingMeta = convRes.data?.metadata || {};
                                        existingMeta.generated_opportunity_cure_doc = pollRes.data.document;
                                        await axios.put(`${API_BASE}/conversations/${conversationId}`, { metadata: existingMeta }, { headers: { 'Authorization': `Bearer ${token}` } });
                                      } catch (e) { console.error('Failed to save opportunity cure doc:', e); }
                                      if (pollRes.data.document.refused) {
                                        toast({ title: "Document Not Applicable", description: "The AI determined this document is not needed for your case.", variant: "destructive", duration: 8000 });
                                      } else {
                                        toast({ title: "Document Generated", description: "Opportunity to Cure document is ready for download." });
                                      }
                                      setIsGeneratingOpportunityCure(false);
                                      return;
                                    } else if (pollRes.data.status === 'failed') {
                                      const _errMsg = pollRes.data.error || 'Generation failed'; throw new Error(_errMsg.toLowerCase().includes('budget') ? 'Your AI key balance has been exceeded. Please go to Profile → Universal Key → Add Balance to continue.' : _errMsg);
                                    }
                                  } catch (pollErr) {
                                    if (pollErr.message) throw pollErr;
                                    console.error('Poll error:', pollErr);
                                  }
                                }
                                throw new Error('Document generation timed out. Please try again.');
                              } else if (res.data.success && res.data.document) {
                                setGeneratedOpportunityCureDoc(res.data.document);
                                toast({ title: "Document Generated", description: "Opportunity to Cure document is ready for download." });
                              }
                            } catch (err) {
                              console.error('Generate failed:', err);
                              toast({ title: "Generation Failed", description: err.message || "Please try again.", variant: "destructive" });
                            } finally {
                              setIsGeneratingOpportunityCure(false);
                            }
                          }}
                          className="px-6"
                          disabled={isGeneratingOpportunityCure}
                          data-testid="generate-opportunity-cure-btn"
                        >
                          {isGeneratingOpportunityCure ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Generating with Claude Sonnet 4.6...
                            </>
                          ) : (
                            'Generate Opportunity to Cure Document'
                          )}
                        </Button>
                      </div>
                    ) : generatedOpportunityCureDoc?.refused ? (
                      <div className="space-y-3">
                        <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="opp-cure-not-applicable">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                            <span className="text-sm font-semibold text-amber-900">Document Not Applicable to Your Case</span>
                          </div>
                          <p className="text-sm text-amber-800">{generatedOpportunityCureDoc.reason || 'The AI determined this document is not needed for your particular case.'}</p>
                        </div>
                        <div className="text-center mt-3">
                          <Button
                            onClick={() => setGeneratedOpportunityCureDoc(null)}
                            variant="outline"
                            size="sm"
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Try Again
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium text-green-800">Opportunity to Cure Document Generated</span>
                        </div>
                        <Card className="border">
                          <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-blue-600" />
                              <div>
                                <p className="text-sm font-medium">{generatedOpportunityCureDoc.filename || 'Opportunity to Cure and Notice of Fault.docx'}</p>
                                <p className="text-xs text-gray-500">Word Document (.docx)</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => {
                                if (generatedOpportunityCureDoc?.content) {
                                  downloadDocxFile(generatedOpportunityCureDoc.content, generatedOpportunityCureDoc.filename || 'Opportunity to Cure and Notice of Fault.docx');
                                  toast({ title: "Document Downloaded", description: "Opportunity to Cure document downloaded." });
                                }
                              }}
                              data-testid="download-opportunity-cure-btn"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </CardContent>
                        </Card>
                        <div className="text-center mt-3">
                          <Button
                            onClick={() => {
                              setGeneratedOpportunityCureDoc(null);
                            }}
                            variant="outline"
                            size="sm"
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            data-testid="regenerate-opportunity-cure-btn"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Regenerate Document
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Mailing Tracker for Second Mailing */}
                {expandedSections.opportunityToCure && (
                  <div className="px-4 pb-4">
                    <MailingTracker
                      conversationId={conversationId}
                      mailingStep="second_mailing"
                      stepLabel="Second Mailing (Opportunity to Cure)"
                      linkedDocuments={['opportunity_to_cure']}
                      onSwitchToStrategy={onSwitchToStrategy}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Tab 4: Administrative Default: Third Mailing - For Defendants and Debt Discharge */}
            {(caseType === 'defendant' || caseType === 'debt_discharge') && (
              <div>
                <button
                  onClick={() => toggleSection('administrativeProcess')}
                  className="w-full p-4 text-left hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className="font-semibold text-gray-900">
                    {caseType === 'debt_discharge' ? '📜 Debt Discharge: Third Mailing (If No Response)' : '📜 Administrative Default: Third Mailing'}
                  </span>
                  {expandedSections.administrativeProcess ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {expandedSections.administrativeProcess && (
                  <div className="px-4 pb-4">

                    {/* Respondent Response Upload - Third Mailing */}
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="response-upload-third-mailing">
                      <h4 className="font-semibold text-blue-900 text-sm flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Did the Respondent Reply to the Second Mailing? (Optional)
                      </h4>
                      <p className="text-xs text-gray-600">
                        If you received a response to your Opportunity to Cure, upload it here. The AI will incorporate their response into the Administrative Default documents, strengthening your position.
                      </p>
                      {!respondentResponse3 ? (
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors text-sm font-medium text-blue-700">
                            <Upload className="w-4 h-4" />
                            Upload Response
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={(e) => handleResponseUpload(e, 'third_mailing')}
                              disabled={isUploadingResponse}
                              data-testid="response-upload-input-third"
                            />
                          </label>
                          {isUploadingResponse && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                          <span className="text-xs text-gray-500">PDF, DOCX, TXT, JPG, PNG (max 50MB)</span>
                        </div>
                      ) : (
                        <div className="bg-white border border-blue-200 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <File className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-900">{respondentResponse3.name}</span>
                              <span className="text-xs text-gray-500">({(respondentResponse3.size / 1024).toFixed(1)} KB)</span>
                            </div>
                            <button onClick={() => removeRespondentResponse('third_mailing')} className="text-gray-400 hover:text-red-500 transition-colors" data-testid="remove-response-third">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          {respondentResponse3.extractedText && (
                            <div className="bg-gray-50 rounded p-2 max-h-32 overflow-y-auto">
                              <p className="text-xs text-gray-500 font-medium mb-1">Extracted Text Preview:</p>
                              <p className="text-xs text-gray-700 whitespace-pre-wrap">{respondentResponse3.extractedText.substring(0, 500)}{respondentResponse3.extractedText.length > 500 ? '...' : ''}</p>
                            </div>
                          )}
                          <p className="text-xs text-green-700 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Response will be addressed in the generated documents
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Administrative Documents Generation Section */}
                    <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <h4 className="text-lg font-semibold text-orange-900 mb-3 flex items-center gap-2">
                        <Gavel className="w-5 h-5" />
                        If No rebuttal, send these Documents
                      </h4>
                      <p className="text-sm text-orange-700 mb-4">
                        {caseType === 'debt_discharge'
                          ? `If the ${selectedCategory === 'tax-collection' ? 'tax collection agency' : 'debt collector'} fails to respond within 21 days, download these documents to establish administrative default:`
                          : 'Generate and download the three administrative process documents to complete your remedy:'
                        }
                      </p>
                      
                      {!generatedTab3Documents ? (
                        <div className="text-center">
                          <Button
                            onClick={handleGenerateTab3Documents}
                            disabled={isGeneratingTab3Documents}
                            className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 font-semibold rounded-lg"
                          >
                            {isGeneratingTab3Documents ? (
                              <>
                                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                Generating Administrative Documents...
                              </>
                            ) : (
                              <>
                                <FileDown className="mr-2 w-4 h-4" />
                                Generate Administrative Documents
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-green-700 font-medium">✅ Administrative Documents Generated Successfully!</p>
                          
                          {/* Administrative Document Download Buttons */}
                          <div className="grid gap-3">
                            <div className="flex items-center justify-between p-3 bg-white rounded border border-orange-200">
                              <div>
                                <h5 className="font-medium text-gray-900">1. Certificate of Non-Response & Rescission</h5>
                                <p className="text-xs text-gray-600">Comprehensive certificate with formal rescission notice - establishes default and revokes presumed consent</p>
                              </div>
                              <Button
                                onClick={() => handleDownloadTab3Document(
                                  clientInfo.country === "The United Kingdom"
                                    ? 'certificate_non_response_uk'
                                    : clientInfo.country === "Canada" 
                                    ? 'certificate_non_response_canada' 
                                    : clientInfo.country === "Australia"
                                    ? 'certificate_non_response_australia'
                                    : 'certificate_of_non_response_rescission', 
                                  'Certificate_of_Non_Response_and_Rescission.docx'
                                )}
                                variant="outline"
                                size="sm"
                                className="bg-orange-600 text-white hover:bg-orange-700"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                            </div>
                            
                            {/* Document 2: Notice of Discharge - Different for Debt Discharge vs Defendant */}
                            {caseType === 'debt_discharge' ? (
                              <div className="flex items-center justify-between p-3 bg-white rounded border-2 border-yellow-400">
                                <div>
                                  <h5 className="font-medium text-gray-900">
                                    2. Notice of Discharge Final
                                    <Badge className="ml-2 bg-yellow-100 text-yellow-800">Debt Discharge</Badge>
                                  </h5>
                                  <p className="text-xs text-gray-600">Post-Conditional Acceptance discharge notice in Equity & English Common Law - closes alleged account</p>
                                </div>
                                <Button
                                  onClick={handleDownloadDebtDischargeNoticeDischargeFinal}
                                  variant="outline"
                                  size="sm"
                                  className="bg-yellow-600 text-white hover:bg-yellow-700"
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Download
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between p-3 bg-white rounded border border-orange-200">
                                <div>
                                  <h5 className="font-medium text-gray-900">2. Notice of Discharge to Court</h5>
                                  <p className="text-xs text-gray-600">Motion to discharge based on administrative default and constitutional violations</p>
                                </div>
                                <Button
                                  onClick={() => handleDownloadTab3Document(
                                    clientInfo.country === "The United Kingdom"
                                      ? 'notice_discharge_court_uk'
                                      : clientInfo.country === "Canada" 
                                      ? 'notice_discharge_court_canada' 
                                      : clientInfo.country === "Australia"
                                      ? 'notice_discharge_court_australia'
                                      : 'notice_of_discharge', 
                                    'Notice_of_Discharge_to_Court.docx'
                                  )}
                                  variant="outline"
                                  size="sm"
                                  className="bg-orange-600 text-white hover:bg-orange-700"
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Download
                                </Button>
                              </div>
                            )}
                            
                            {/* Document 3: Final Administrative Closure Notice - NOT for Debt Discharge */}
                            {caseType !== 'debt_discharge' && (
                              <div className="flex items-center justify-between p-3 bg-white rounded border border-orange-200">
                                <div>
                                  <h5 className="font-medium text-gray-900">3. Final Administrative Closure Notice</h5>
                                  <p className="text-xs text-gray-600">Final act to seal and complete the entire administrative process</p>
                                </div>
                                <Button
                                  onClick={() => handleDownloadTab3Document(
                                    clientInfo.country === "The United Kingdom"
                                      ? 'final_administrative_closure_uk'
                                      : clientInfo.country === "Canada" 
                                      ? 'final_administrative_closure_canada' 
                                      : clientInfo.country === "Australia"
                                      ? 'final_administrative_closure_australia'
                                      : 'final_closure_notice', 
                                    'Final_Administrative_Closure_Notice.docx'
                                  )}
                                  variant="outline"
                                  size="sm"
                                  className="bg-orange-600 text-white hover:bg-orange-700"
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Download
                                </Button>
                              </div>
                            )}
                          </div>
                          
                          {/* US-Only Disclosure - Complete Process Status with Article I Section 10 */}
                          {clientInfo.country === "United States" ? (
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                              <p className="text-xs text-amber-800">
                                <strong>Complete Process Status:</strong> These documents complete your full administrative remedy. 
                                All obligations discharged in honor per Article I Section 10 Clause 1.
                              </p>
                            </div>
                          ) : (
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                              <p className="text-xs text-amber-800">
                                <strong>Complete Process Status:</strong> These documents complete your full administrative remedy. 
                                All obligations discharged in honor through equity and common law principles.
                              </p>
                            </div>
                          )}
                          
                          <div className="text-center mt-4">
                            <Button
                              onClick={handleGenerateTab3Documents}
                              variant="outline"
                              size="sm"
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Regenerate Administrative Documents
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Mailing Tracker for Third Mailing */}
                {expandedSections.administrativeProcess && (
                  <div className="px-4 pb-4">
                    <MailingTracker
                      conversationId={conversationId}
                      mailingStep="third_mailing"
                      stepLabel="Third Mailing (Administrative Default)"
                      linkedDocuments={['certificate_of_non_response', 'notice_of_administrative_default']}
                      onSwitchToStrategy={onSwitchToStrategy}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Mailing Progress Tracker */}
            {(caseType === 'defendant' || caseType === 'debt_discharge') && remedy && (
              <div className="px-4 py-3">
                <MailingProgressTracker conversationId={conversationId} />
              </div>
            )}

            {/* Tab 6: Escalate to Court */}
            <div className="border-b">
              <button
                onClick={() => toggleSection('escalateToCourt')}
                className="w-full p-4 text-left hover:bg-gray-50 flex items-center justify-between"
              >
                <span className="font-semibold text-gray-900">
                  {caseType === 'debt_discharge' ? '⚖️ Escalate to Court: Judicial Enforcement' : '⚖️ Escalate to Court: Bill of Complaint'}
                </span>
                {expandedSections.escalateToCourt ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {expandedSections.escalateToCourt && (
                <div className="px-4 pb-4">
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 space-y-4">
                      <h3 className="text-lg font-bold text-blue-900">Filing a Bill of Complaint for Equitable Relief</h3>
                      <p className="text-sm text-blue-800 leading-relaxed">
                        After completing your administrative process — sending your Conditional Acceptance, issuing the Opportunity to Cure, 
                        and establishing administrative default through silence — you have built a <strong>comprehensive record of dishonor</strong>. 
                        If the opposing party still refuses to act in honor, your next step is to <strong>escalate to the United States District Court</strong> 
                        by filing a <strong>Bill of Complaint (Verified Complaint for Equitable Relief)</strong>.
                      </p>
                      
                      <div className="space-y-3">
                        <h4 className="text-sm font-bold text-blue-900">What is a Bill of Complaint?</h4>
                        <p className="text-sm text-blue-800">
                          A Bill of Complaint is the formal pleading that initiates a case in <strong>Equity</strong>. Unlike a standard civil complaint 
                          that relies on statutory codes, a Bill of Complaint invokes the court's <strong>equitable jurisdiction</strong> — the power 
                          to deliver justice when there is no adequate remedy at law. It is rooted in the traditions of English Common Law, 
                          the Magna Carta (1215), and the maxims of Equity Jurisprudence.
                        </p>
                        
                        <h4 className="text-sm font-bold text-blue-900">Why U.S. District Court?</h4>
                        <p className="text-sm text-blue-800">
                          The U.S. District Court is a court of record with original jurisdiction over cases arising under the Constitution. 
                          When you invoke <strong>Article I, Section 10, Clause 1</strong> and seek relief grounded in Equity, you are asking 
                          the federal court to recognize your administrative record and enforce the default that was established through proper notice, 
                          due process, and the opposing party's own silence.
                        </p>

                        <h4 className="text-sm font-bold text-blue-900">What Makes Your Complaint Powerful?</h4>
                        <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                          <li><strong>You did not start at court</strong> — you exhausted the administrative process first</li>
                          <li><strong>You gave every opportunity</strong> to respond, cure, and settle privately</li>
                          <li><strong>You have certified proof</strong> of every notice, every deadline, every silence</li>
                          <li><strong>Default is already established</strong> — the court is being asked to recognize it, not create it</li>
                          <li><strong>You come with clean hands</strong> — the foundational requirement of Equity</li>
                        </ul>

                        <h4 className="text-sm font-bold text-blue-900">Your Complaint Will Include:</h4>
                        <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                          <li>Proper court caption and jurisdiction statement</li>
                          <li>Identification of all parties</li>
                          <li>Chronological statement of facts from your administrative record</li>
                          <li>Each cause of action as a separate "Count"</li>
                          <li>Exhibit references to every notice, receipt, and piece of evidence</li>
                          <li>The default/non-response narrative</li>
                          <li>Prayer for specific equitable relief</li>
                          <li>Verification under penalty of perjury</li>
                        </ul>
                      </div>
                    </div>

                    <div className="text-center py-3">
                      <Button
                        onClick={() => { window.location.href = `/dashboard/complaint-portal/${conversationId}`; }}
                        className="px-8 bg-blue-700 hover:bg-blue-800"
                        data-testid="generate-complaint-portal-btn"
                      >
                        <Scale className="w-4 h-4 mr-2" />
                        Generate Bill of Complaint
                      </Button>
                      <p className="text-xs text-gray-500 mt-2">
                        Opens the Complaint Builder with your case data pre-loaded
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tab 4: Mailing Requirements */}
            <div>
              <button
                onClick={() => toggleSection('mailingRequirements')}
                className="w-full p-4 text-left hover:bg-gray-50 flex items-center justify-between"
              >
                <span className="font-semibold text-gray-900">📮 Mailing Requirements</span>
                {expandedSections.mailingRequirements ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {expandedSections.mailingRequirements && (
                <div className="px-4 pb-4">
                  <div className="bg-blue-50 p-4 rounded text-sm max-h-96 overflow-y-auto mb-4">
                    <div className="space-y-4">
                      <h3 className="text-lg font-bold text-blue-800 mb-4">MAILING REQUIREMENTS</h3>
                      
                      <div className="space-y-4">
                        <div className="border-l-4 border-blue-600 pl-4">
                          <h4 className="font-semibold text-blue-800">1. Certified Mail Requirement</h4>
                          <div className="text-blue-700 space-y-1">
                            <p><strong>a.</strong> Always recommend sending documents by Certified Mail through the U.S. Postal Service.</p>
                            <p><strong>b.</strong> This creates a verifiable tracking number and record of delivery.</p>
                          </div>
                        </div>
                        
                        <div className="border-l-4 border-blue-600 pl-4">
                          <h4 className="font-semibold text-blue-800">2. Green Return Receipt (PS Form 3811)</h4>
                          <div className="text-blue-700 space-y-1">
                            <p><strong>a.</strong> Instruct the user to include the green return slip so that the receiving party must sign for the delivery.</p>
                            <p><strong>b.</strong> The signed receipt becomes evidence that the other party received the notice.</p>
                          </div>
                        </div>
                        
                        <div className="border-l-4 border-blue-600 pl-4">
                          <h4 className="font-semibold text-blue-800">3. Why It Matters</h4>
                          <div className="text-blue-700 space-y-1">
                            <p><strong>a.</strong> Provides a chain of custody and undeniable record of service.</p>
                            <p><strong>b.</strong> Shifts the burden of proof — once delivered, the other party cannot claim ignorance.</p>
                            <p><strong>c.</strong> Supports the principle of Equity: <em>"Equity regards as done that which ought to be done."</em></p>
                          </div>
                        </div>
                        
                        <div className="border-l-4 border-blue-600 pl-4">
                          <h4 className="font-semibold text-blue-800">4. Administrative Enforcement</h4>
                          <div className="text-blue-700 space-y-1">
                            <p><strong>a.</strong> Emphasize that without proof of service, the Administrative Process can be challenged.</p>
                            <p><strong>b.</strong> With certified mailing and return receipt, every step — Conditional Acceptance, Notice of Default, Certificate of Non-Response — is enforceable as a lawful record.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6 p-4 bg-blue-100 border border-blue-300 rounded">
                        <div className="text-center">
                          <h4 className="font-bold text-blue-900 mb-3">📝 Final Reminder for Protection</h4>
                          <p className="text-sm text-blue-800 font-medium italic">
                            "For your protection and enforcement in honor, send all documents via Certified Mail with green return receipt slip. 
                            This ensures a lawful record and prevents the opposing party from denying receipt."
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-green-50 border border-green-300 rounded">
                        <p className="text-xs text-green-800">
                          <strong>Professional Tip:</strong> Keep copies of all certified mail receipts and return receipts as part of your case file. 
                          These documents serve as evidence in any future proceedings and demonstrate your diligent adherence to proper legal process.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Court Room Script Section */}
      {remedy && (
        <Card className="border-2 border-purple-200">
          <CardHeader className="bg-purple-50">
            <CardTitle className="text-xl text-purple-900 flex items-center gap-2">
              <Gavel className="w-6 h-6" />
              Court Room Script
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-200">
              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                    <FileText className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-purple-900 mb-2">
                    Court Room Script & Guidance
                  </h3>
                  <p className="text-purple-700 mb-4">
                    Download your personalized court room script to confidently represent yourself in legal proceedings. 
                    This script includes proper lawful language, objections, and procedural guidance.
                  </p>
                </div>

                <div className="flex justify-center">
                  <Button
                    onClick={handleDownloadCourtRoomScript}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    <Download className="mr-2 w-5 h-5" />
                    Download Court Room Script
                  </Button>
                </div>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-xs text-yellow-800">
                    <strong>Important:</strong> Review the script thoroughly before your court appearance. 
                    Practice the key phrases and understand the legal concepts to present your case effectively.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Action Buttons */}
      {remedy && (
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={handleRefineRemedy}
            variant="outline"
            className="px-6 py-3 font-semibold rounded-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <RefreshCw className="mr-2 w-4 h-4" />
            Refine Remedy
          </Button>

          <Button
            onClick={handleDownload}
            variant="outline"
            className="bg-gray-700 text-white hover:bg-gray-800 px-6 py-3 font-semibold rounded-lg"
          >
            <Download className="mr-2 w-4 h-4" />
            Download Full Report
          </Button>
        </div>
      )}

      {/* Subscription Upgrade Prompt for Free Users */}
      {user?.subscription_plan === 'free' && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Unlock Advanced Legal Document Generation
            </h3>
            <p className="text-blue-700 mb-4">
              Upgrade to Justice Seeker, Sovereign Shield, or the Defender of the People Subscription for more comprehensive document templates, advanced analysis, and unlimited legal matters.
            </p>
            <Button 
              className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
              onClick={() => {
                // Navigate to Subscription Management Hub
                window.location.href = '/dashboard/subscription';
              }}
            >
              Upgrade Plan
            </Button>
          </CardContent>
        </Card>
      )}
        </>
      )}
    </div>
  );
};

export default WorkflowBuilder;